import { Fragment, useState, useMemo, useCallback, useEffect, useRef, type DragEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  format,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  getDay,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Ban,
  Info,
  Loader2,
  User,
  Clock,
  CalendarDays,
  Plus,
  Circle,
  SquareCheck,
  Repeat,
  MapPinned,
  X,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { Sheet, SheetContent, SheetTitle } from '@/components/atoms/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { BoardListView } from '@/components/dispatch/BoardListView';
import { DispatchMapPanel } from '@/components/dispatch/DispatchMapPanel';
import { JobsBoardView as JobsBoardLens } from '@/components/organisms/jobs/JobsBoardView';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
// Named timeZone support: core only handles 'local'/'UTC' — without this plugin a
// named business tz silently falls back to UTC and shifts events (+offset bug).
import luxonPlugin from '@fullcalendar/luxon3';
import type {
  EventInput,
  EventClickArg,
  EventContentArg,
  DayHeaderContentArg,
  DateSelectArg,
} from '@fullcalendar/core';
import {
  getStatusBlockClasses,
  getStatusDotClass,
  getStatusBlockTint,
  getStatusBarClass,
} from '@/components/molecules/status-tokens';
import { totalDayCapacity, capacityChipClasses } from '@/components/dispatch/capacity';
import { JobTypeIcon } from '@/components/dispatch/JobTypeIcon';
import { TileBadges } from '@/components/dispatch/TileBadges';
import { JobTagChips } from '@/components/dispatch/JobTagChips';
import { JobHoverCard } from '@/components/dispatch/JobHoverCard';
import { CompletedTileCheck } from '@/components/dispatch/CompletedTileCheck';
import {
  bookingJobTypeStyle,
  isShortTile,
  resolveJobTypeHex,
  liveStatusLabel,
} from '@/components/dispatch/tech-colors';
import { ScheduleWorkspaceShell } from '@/components/layouts';
import { ScheduleTimezoneToggle } from '@/components/molecules/ScheduleTimezoneToggle';
import { BookingDetailSheet } from '@/components/calendar/BookingDetailSheet';
import {
  UNSCHEDULED_DND_TYPE,
  BOOKING_MOVE_DND_TYPE,
} from '@/components/calendar/CalendarUnscheduledPanel';
import { CalendarSideRail } from '@/components/calendar/CalendarSideRail';
import { UnsequencedChip } from '@/components/calendar/UnsequencedChip';
import { CalendarFiltersDrawer } from '@/components/calendar/CalendarFiltersDrawer';
import { selectCancelledBookings } from '@/lib/scheduler/cancelledBookings';
import { blockedTimeLabel, blockedTimeTooltip } from '@/lib/scheduler/timeOff';
import {
  CALENDAR_VIEW_TYPES,
  resolveCalendarView,
  type CalendarViewType,
} from '@/lib/scheduler/calendarView';
import {
  EMPTY_CALENDAR_FILTERS,
  activeFilterCount,
  matchesFilters,
  type CalendarFilters,
} from '@/lib/scheduler/calendarFilters';
import { DEFAULT_CONTACT_LIST_FILTERS } from '@/components/organisms/contacts/contact-list-utils';
import { DEFAULT_LEAD_LIST_FILTERS } from '@/components/organisms/leads/LeadListPanel';
import {
  VisitRequestForm,
  type VisitRequestFormValues,
} from '@/components/organisms/scheduler/VisitRequestForm';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useCreateBookingFlow } from '@/hooks/scheduler/useCreateBookingFlow';
import { useScheduleBooking } from '@/hooks/scheduler/useScheduleBooking';
import { useMoveBooking } from '@/hooks/scheduler/useMoveBooking';
import { useConflictOverride } from '@/hooks/scheduler/useConflictOverride';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useUnscheduledBookings } from '@/hooks/scheduler/useUnscheduledBookings';
import { useSchedulerCrews } from '@/hooks/scheduler/useSchedulerCrews';
import { useServiceTypes } from '@/hooks/scheduler/useServiceTypes';
import { useJobTypeFamilyResolver } from '@/hooks/scheduler/useJobTypeFamilyResolver';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';
import { usePermissionScope } from '@/hooks/usePermissions';
import { useJobTypes } from '@/hooks/settings/useJobTypes';
import { taskTypeOption } from '@/lib/tasks/taskTypes';
import { useTasks } from '@/hooks/tasks';
import { operationsClient, apiGet } from '@/lib/api';
import { schedulerEndpoints, schedulingPrefix } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import {
  isTaskComplete,
  isTaskOverdue,
  normalizeTaskStatus,
  parseTaskDue,
  taskDayKey,
} from '@/lib/tasks/taskModel';
import type {
  SchedulerBooking,
  SchedulerTechnician,
  BookingListResponse,
  AppointmentStatus,
  SchedulerCrew,
} from '@/types/scheduler';
import type { Task } from '@/types/task';
import { cn } from '@/lib/utils';
import { dateKeyInTimeZone, formatDateTimeInTimeZone, zonedDateTimeToUtc } from '@/lib/date-values';
import { useScheduleTimezone } from '@/lib/time/scheduleTimezone';

type ViewType = CalendarViewType;
type ScheduledBooking = SchedulerBooking & { startAt: string; endAt: string };
type CalendarTasksByDay = Map<string, Task[]>;
type CreateBookingPrefill = {
  date?: string;
  technicianId?: string;
  startAt?: string;
};

// NOT-326 — DnD payload for dragging a job between Technician-view cells (reassign
// across rows / reschedule across columns). BOOKING_MOVE_DND_TYPE is imported from
// the panel (shared with the Job tray's "No technician" cards).
const TECH_MOVE_DND_TYPE = 'application/x-notifi-tech-move';
const UNASSIGNED_KEY = '__unassigned__';

export const Route = createFileRoute('/_dashboard/dashboard/calendar/')({
  validateSearch: (search: Record<string, unknown>) => ({
    // Day moved to /dashboard/dispatch; Calendar is week/month planning + a List lens.
    // Undefined when not pinned in the URL → the component falls back to the
    // tenant's defaultCalendarView (NOT-322), then 'week'.
    view: (CALENDAR_VIEW_TYPES.includes(search.view as ViewType)
      ? (search.view as ViewType)
      : undefined) as ViewType | undefined,
    // No browser-clock default — the component fills it from the business tz so a
    // viewer in another zone never lands on the wrong business day near midnight.
    date:
      typeof search.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.date)
        ? search.date
        : undefined,
    tasks: search.tasks === false || search.tasks === 'false' ? false : true,
    bookingId: typeof search.bookingId === 'string' ? search.bookingId : undefined,
  }),
  component: CalendarPage,
});

function toDateString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Visible-window fallback when business hours aren't configured (mirrors DispatchBoard). */
const FALLBACK_OPEN = '07:00';
const FALLBACK_CLOSE = '19:00';

/** 'HH:mm' (± an hour of padding) → clamped 'HH:mm:ss' slot bound. */
function paddedBound(time: string, padHours: number): string {
  const [h = '0', m = '0'] = time.split(':');
  const clamped = Math.max(0, Math.min(24 * 60, Number(h) * 60 + Number(m) + padHours * 60));
  const hh = Math.floor(clamped / 60)
    .toString()
    .padStart(2, '0');
  const mm = (clamped % 60).toString().padStart(2, '0');
  return `${hh}:${mm}:00`;
}

/** Wall-clock 'HH:mm:ss' of an instant in the given tz, ALWAYS 00–23 hours.
 *  `hourCycle: 'h23'` is required: with only `hour12: false` some engines resolve
 *  to an h24 cycle and return `24:xx` at midnight, which breaks the lexical
 *  min/max comparisons that drive the week window (NOT-321). */
function wallTimeInTz(iso: string, tz: string): string {
  return formatDateTimeInTimeZone(iso, tz, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
}

/** Round an 'HH:mm:ss' down/up to the 30-min grid, clamped to 00:00:00–24:00:00.
 *  NOT-321 — so an out-of-hours event sits fully inside the widened window. */
function roundToHalfHour(time: string, dir: 'floor' | 'ceil'): string {
  const [h = '0', m = '0', s = '0'] = time.split(':');
  // Round in SECONDS so a ceil of e.g. 20:00:01 lands on 20:30:00 — FC's
  // slotMaxTime is exclusive, so dropping the seconds would still clip a visit
  // ending just past the half-hour (NOT-321).
  const totalSec = Number(h) * 3600 + Number(m) * 60 + Number(s);
  const half = 30 * 60;
  const roundedSec =
    dir === 'floor' ? Math.floor(totalSec / half) * half : Math.ceil(totalSec / half) * half;
  const clamped = Math.max(0, Math.min(24 * 3600, roundedSec));
  const hh = Math.floor(clamped / 3600)
    .toString()
    .padStart(2, '0');
  const mm = Math.floor((clamped % 3600) / 60)
    .toString()
    .padStart(2, '0');
  return `${hh}:${mm}:00`;
}

/** "Today" (YYYY-MM-DD) in the given tz — the calendar renders in business time,
 *  so the default day + the Today button must follow it, not the browser clock. */
function todayInTz(tz: string | null): string {
  if (!tz) return toDateString(new Date());
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

const STATUS_BADGE_VARIANT: Record<
  AppointmentStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  UNSCHEDULED: 'outline',
  RESERVED: 'default',
  CONFIRMED: 'secondary',
  SCHEDULED: 'default',
  DISPATCHED: 'secondary',
  IN_PROGRESS: 'secondary',
  ON_HOLD: 'outline',
  COMPLETED: 'outline',
  SKIPPED: 'outline',
  CANCELLED: 'destructive',
  CANCELLATION_PENDING: 'outline',
  EXPIRED: 'outline',
};

function formatTimeRange(
  startAt: string | null,
  endAt: string | null,
  timezone?: string | null,
): string {
  if (!startAt || !endAt) return 'Unscheduled';
  const start = formatDateTimeInTimeZone(startAt, timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const end = formatDateTimeInTimeZone(endAt, timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return start && end ? `${start} - ${end}` : '';
}

/** §15 — series-highlight match: 'series:<id>' rings exact series days,
 *  'job:<id>' rings all visits of a recurring/multi-visit job. */
function isSeriesSibling(
  b: { seriesId?: string | null; jobId?: string | null },
  key: string | undefined,
): boolean {
  if (!key) return false;
  return key.startsWith('series:')
    ? `series:${b.seriesId ?? ''}` === key
    : `job:${b.jobId ?? ''}` === key;
}

function getBookingDayKey(iso: string | null, timezone?: string | null): string {
  if (!iso) return '';
  return dateKeyInTimeZone(iso, timezone);
}

// NOT-330 — a (tech, day)'s full-day unavailability: an approved time-off /
// holiday block covering the day (typed label + tooltip), or a non-working
// weekday from availability (generic "Unavailable"), else null. Shared by the
// Technician grid (per-cell) and the Week/Month aggregate ("N off") counts.
// Partial blocks (lunch / external "busy") aren't full-day, so they don't blank
// the day — the time-grid board renders those precisely.
function techDayUnavailability(
  tech: SchedulerTechnician,
  day: Date,
  businessTz: string | null,
): { label: string; tooltip: string } | null {
  const dayKey = toDateString(day);
  for (const block of tech.blockedTimes ?? []) {
    if (block.source !== 'TIME_OFF' && block.source !== 'HOLIDAY') continue;
    const startKey = getBookingDayKey(block.startAt, businessTz);
    const endKey = block.endAt ? getBookingDayKey(block.endAt, businessTz) : startKey;
    if (startKey && startKey <= dayKey && dayKey <= (endKey || startKey)) {
      return { label: blockedTimeLabel(block).label, tooltip: blockedTimeTooltip(block) };
    }
  }
  const entry = tech.availabilityDays?.find((a) => a.dayOfWeek === getDay(day));
  if (entry && !entry.isAvailable) return { label: 'Unavailable', tooltip: 'Not working' };
  return null;
}

// Date-only +1 (tz-agnostic): FullCalendar's all-day end is exclusive, so a job
// covering its last day needs end = lastDay + 1 (NOT-316).
function nextDayKey(dayKey: string): string {
  const d = new Date(`${dayKey}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function isScheduledBooking(booking: SchedulerBooking): booking is ScheduledBooking {
  return Boolean(booking.startAt && booking.endAt);
}

/**
 * NOT-328 — truly unassigned: no primary tech, no helper techs (crew assignment),
 * and no crew. `!technicianId` alone is too weak — a crew/helper-assigned booking
 * still carries `technicianIds`/`crew` (which `matchesFilters` honors), so it
 * must not land in the "No technician" tray bucket.
 */
function isUnassignedBooking(b: SchedulerBooking): boolean {
  return !b.technicianId && !(b.technicianIds && b.technicianIds.length) && !b.crew;
}

/** Short weekday labels indexed 0=Sun … 6=Sat (rotated by the tenant week-start). */
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** NOT-322 — tenant week-start (0–6, default Monday) for date-fns / FullCalendar. */
function asWeekStart(day: number | undefined): 0 | 1 | 2 | 3 | 4 | 5 | 6 {
  const d = day ?? 1;
  return (d >= 0 && d <= 6 ? d : 1) as 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

function sortTasksForCalendar(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const dueA = parseTaskDue(a.dueDate);
    const dueB = parseTaskDue(b.dueDate);
    if (dueA?.hasTime && dueB?.hasTime) return dueA.date.getTime() - dueB.date.getTime();
    if (dueA?.hasTime) return -1;
    if (dueB?.hasTime) return 1;
    return a.title.localeCompare(b.title);
  });
}

/**
 * "2:30 PM · Call Jennifer" — the time in the zone the calendar is drawn in,
 * so a chip never disagrees with the grid (formatting in the browser's zone
 * put a 1:42 PM ET task at "2:42 PM" for a dispatcher in Buenos Aires).
 */
function formatCalendarTaskLabel(task: Task, timeZone: string): string {
  const due = parseTaskDue(task.dueDate);
  // Only a timed due is an instant; a date-only one has no time to show.
  const dueAt = due?.hasTime ? task.dueDate : null;
  const prefix = dueAt
    ? `${formatDateTimeInTimeZone(dueAt, timeZone, { hour: 'numeric', minute: '2-digit' })} · `
    : '';
  return `${prefix}${task.title}`;
}

function getTaskAssigneeName(task: Task): string | null {
  const assignee = task.assignedUser;
  if (!assignee) return task.technician?.name ?? null;
  return [assignee.firstName, assignee.lastName].filter(Boolean).join(' ').trim() || null;
}

/**
 * A task that takes a slot of time: a due time AND a duration ("call at 2:30
 * for 15 min"). These render as blocks on the time grid; every other task
 * stays a chip in the all-day row. Blocks never reserve the technician —
 * scheduling does not read tasks.
 */
function taskTimeBlock(task: Task): { start: string; end: string } | null {
  if (!task.durationMinutes || !task.dueDate) return null;
  const due = parseTaskDue(task.dueDate);
  if (!due?.hasTime) return null;
  return {
    start: due.date.toISOString(),
    end: new Date(due.date.getTime() + task.durationMinutes * 60_000).toISOString(),
  };
}

function normalizeSchedulerBooking(booking: SchedulerBooking): SchedulerBooking {
  if (!booking.startAt && !booking.endAt && booking.status === 'RESERVED') {
    return { ...booking, status: 'UNSCHEDULED' };
  }
  return booking;
}

function normalizeBookingListResponse(raw: unknown): BookingListResponse {
  const payload = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const items = Array.isArray(raw)
    ? raw
    : (payload.items ?? payload.data ?? payload.bookings ?? payload.detailData);
  const data = Array.isArray(items)
    ? (items as SchedulerBooking[]).map(normalizeSchedulerBooking)
    : [];

  return {
    data,
    total: Number(payload.total ?? data.length),
    page: Number(payload.page ?? 1),
    limit: Number(payload.limit ?? data.length),
  };
}

function formatStatusLabel(status: string): string {
  return status
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Small ↻ marker for bookings whose parent Job is recurring. */
function RecurringBadge({ className }: { className?: string }) {
  return (
    <Repeat
      aria-label="Recurring visit"
      className={cn('inline-block size-3 shrink-0 text-muted-foreground', className)}
    />
  );
}

// NOT-329 — tile helpers shared by the calendar views.
/** "Visit N of M" subtitle, or null for single-visit jobs / missing data. */
function visitLabel(b: SchedulerBooking): string | null {
  return b.visitNumber && b.visitTotal && b.visitTotal > 1
    ? `Visit ${b.visitNumber} of ${b.visitTotal}`
    : null;
}

/** Customer name, falling back to the address when missing (§8 edge case). */
function tileTitle(b: SchedulerBooking): string {
  return b.customerName || b.address || 'Job';
}

const isPendingWeatherHold = (booking: SchedulerBooking) =>
  booking.weatherHoldMode === 'PENDING' &&
  Boolean(booking.weatherHeldAt) &&
  !booking.weatherHoldReleasedAt;

/** Up-to-2-letter initials for a small tech avatar. */
function nameInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function CalendarStatusPill({ status }: { status: AppointmentStatus }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-[3px] border px-2 py-1 text-xs font-normal leading-none',
        getStatusBlockClasses(status),
      )}
    >
      {formatStatusLabel(status)}
    </span>
  );
}

function CalendarPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { submit: submitBooking, reserveSlot: reserveBookingSlot } = useCreateBookingFlow();

  const {
    data: businessSettings,
    isError: settingsError,
    isFetching: settingsFetching,
    refetch: refetchSettings,
  } = useBusinessSettings();
  const businessTz = businessSettings?.timezone ?? null;
  const scheduleDisplay = useScheduleTimezone();
  const displayTz = scheduleDisplay.displayTimezone;
  // Gate everything (date derivation, queries, the grid) on a resolved business tz
  // so a cross-tz viewer never mounts/fetches the wrong day before /settings loads.
  const dateReady = businessTz !== null;

  // NOT-322 — freeze the tenant default once this mount's settings fetch has
  // SETTLED (not while a background refetch is in flight). Two requirements:
  //   1. Open on the *current* tenant default — so don't resolve from a stale
  //      cached value React Query hands back during the 60s staleTime refetch
  //      (that would open on the cached default and then flip when fresh arrives).
  //   2. Precedence applies on OPEN only — once frozen, a later refetch or an
  //      office-settings save must not recompute the fallback and jump the view.
  // URL `?view` always wins; without it we hold the view until settings settle.
  const frozenTenantDefault = useRef<{ value: string | undefined } | null>(null);
  if (frozenTenantDefault.current === null && businessSettings && !settingsFetching) {
    frozenTenantDefault.current = { value: businessSettings.defaultCalendarView };
  }
  // With no pinned `?view`, the fallback isn't known until the first settled
  // settings result — render a loading state until then rather than guessing from
  // cached settings (which could be stale).
  const viewReady = Boolean(search.view) || frozenTenantDefault.current !== null;
  // URL `?view` wins; otherwise the frozen tenant default, then 'week' (NOT-322).
  const view: ViewType = resolveCalendarView(search.view, frozenTenantDefault.current?.value);
  const weekStartsOn = asWeekStart(businessSettings?.weekStartDay);
  const workWeekStartsOn = asWeekStart(businessSettings?.workWeekStartDay);
  // Default "today" in the business tz (the zone the grid renders in), not the
  // browser clock — so a viewer in another zone doesn't land on the wrong day.
  const currentDate = search.date ?? todayInTz(displayTz);
  const tasksEnabled = search.tasks;
  // NOT-326 — tasks render on the time-grid/month views and in each
  // technician's row of the Technician view; List/Board have no task surface,
  // so derive whether tasks are actually shown instead of mutating the `tasks`
  // URL param (which would inject a history entry and trap the Back button).
  const tasksVisible =
    tasksEnabled &&
    (view === 'week' || view === 'workweek' || view === 'month' || view === 'technician');
  const dateObj = useMemo(() => parseLocalDate(currentDate), [currentDate]);

  // Pin the implicit date into the URL as the business-tz day once the tz is known
  // (replace, no history entry) so nav never operates on the browser-clock fallback.
  useEffect(() => {
    if (!search.date && businessTz) {
      void navigate({
        search: (prev) => ({ ...prev, date: todayInTz(displayTz) }),
        replace: true,
      });
    }
  }, [search.date, businessTz, displayTz, navigate]);

  const [selectedBooking, setSelectedBooking] = useState<SchedulerBooking | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  // The map only opens in the grid views that expose the Map button (Week / Work
  // week / Month / Technician — not List/Board). Gate the dock + drawer on this
  // too so an open map can't persist (eating 360px) after switching to a tab
  // that has no Map button to close it.
  const mapSupported =
    view === 'week' || view === 'workweek' || view === 'month' || view === 'technician';
  // The map docks beside the grid as an `lg:flex` <aside>; below lg there's no
  // room, so it opens as a right-side drawer. Gate on the SAME lg breakpoint the
  // aside's CSS uses (not the md-based useIsDesktop) — otherwise at 768–1023px
  // the drawer is suppressed while the aside is still CSS-hidden → no map shows.
  const canDockMap = useIsLargeScreen();
  const [expandedMonthDay, setExpandedMonthDay] = useState<string | null>(null);
  // NOT-320 — multi-select calendar filters (assignee/status/event type/job type/
  // skills), applied client-side to every surface. Component state, not persisted
  // (the ticket doesn't ask to remember filters; they reset on reload).
  const [filters, setFilters] = useState<CalendarFilters>(EMPTY_CALENDAR_FILTERS);
  // NOT-320/326 — filters apply to week/work week/month/technician grids. On
  // List/Board (and the mobile agenda on those tabs) the drawer is hidden, so any
  // carried-over filter would be stuck with no control to clear it → reset filters
  // (component state, no history entry) when leaving the filterable grids.
  useEffect(() => {
    const filterableGrid =
      view === 'week' || view === 'workweek' || view === 'month' || view === 'technician';
    if (!filterableGrid && activeFilterCount(filters) > 0) setFilters(EMPTY_CALENDAR_FILTERS);
  }, [view, filters]);
  const [createDrawer, setCreateDrawer] = useState<{
    open: boolean;
    prefill?: CreateBookingPrefill;
  }>({ open: false });

  const openCreateDrawer = useCallback((prefill?: CreateBookingPrefill) => {
    setCreateDrawer({ open: true, prefill });
  }, []);

  const closeCreateDrawer = useCallback(() => {
    setCreateDrawer({ open: false });
  }, []);

  // Compute date range for the current view
  const { dateFrom, dateTo } = useMemo(() => {
    // NOT-326 — the Technician view spans the same 7-day week as Week.
    if (view === 'week' || view === 'technician') {
      const weekStart = startOfWeek(dateObj, { weekStartsOn });
      const weekEnd = endOfWeek(dateObj, { weekStartsOn });
      return { dateFrom: toDateString(weekStart), dateTo: toDateString(weekEnd) };
    }
    if (view === 'workweek') {
      // NOT-323 — 5 consecutive days starting on the configured work-week start.
      const wwStart = startOfWeek(dateObj, { weekStartsOn: workWeekStartsOn });
      return { dateFrom: toDateString(wwStart), dateTo: toDateString(addDays(wwStart, 4)) };
    }
    // month
    const monthStart = startOfMonth(dateObj);
    const monthEnd = endOfMonth(dateObj);
    return { dateFrom: toDateString(monthStart), dateTo: toDateString(monthEnd) };
  }, [view, dateObj, weekStartsOn, workWeekStartsOn]);

  const { taskDateFrom, taskDateTo } = useMemo(
    () => ({
      taskDateFrom: toDateString(subDays(parseLocalDate(dateFrom), 1)),
      taskDateTo: toDateString(addDays(parseLocalDate(dateTo), 1)),
    }),
    [dateFrom, dateTo],
  );
  const bookingFetchRange = useMemo(
    () =>
      scheduleDisplay.mode === 'browser'
        ? {
            from: toDateString(subDays(parseLocalDate(dateFrom), 1)),
            to: toDateString(addDays(parseLocalDate(dateTo), 1)),
          }
        : { from: dateFrom, to: dateTo },
    [dateFrom, dateTo, scheduleDisplay.mode],
  );

  // Navigation
  const goToPrev = useCallback(() => {
    void navigate({
      search: (prev) => {
        const d = parseLocalDate(prev.date ?? currentDate);
        const next =
          view === 'week' || view === 'workweek' || view === 'technician'
            ? subWeeks(d, 1)
            : subMonths(d, 1);
        return { ...prev, date: toDateString(next) };
      },
    });
  }, [navigate, view, currentDate]);

  const goToNext = useCallback(() => {
    void navigate({
      search: (prev) => {
        const d = parseLocalDate(prev.date ?? currentDate);
        const next =
          view === 'week' || view === 'workweek' || view === 'technician'
            ? addWeeks(d, 1)
            : addMonths(d, 1);
        return { ...prev, date: toDateString(next) };
      },
    });
  }, [navigate, view, currentDate]);

  const goToToday = useCallback(() => {
    void navigate({ search: (prev) => ({ ...prev, date: todayInTz(displayTz) }) });
  }, [navigate, displayTz]);

  const setView = useCallback(
    (v: ViewType) => {
      // Pin the chosen view in the URL for this session only. We intentionally
      // don't persist a last-view: the tenant's Default calendar view wins on a
      // fresh open (NOT-322 spec — URL `?view` > tenant default > 'week').
      void navigate({ search: (prev) => ({ ...prev, view: v }) });
    },
    [navigate],
  );

  const toggleTasks = useCallback(() => {
    void navigate({ search: (prev) => ({ ...prev, tasks: !tasksEnabled }) });
  }, [navigate, tasksEnabled]);

  // ─── Data fetching ──────────────────────────────────────────────────────────

  const { data: bookingsRaw, isFetching: bookingsLoading } = useApiQuery<BookingListResponse>({
    queryKey: schedulerKeys.bookings({
      dateFrom: bookingFetchRange.from,
      dateTo: bookingFetchRange.to,
    }),
    queryFn: async () => {
      const raw = await apiGet<unknown>(operationsClient, schedulerEndpoints.bookings(), {
        params: {
          dateFrom: bookingFetchRange.from,
          dateTo: bookingFetchRange.to,
          limit: 100,
        },
      });
      return normalizeBookingListResponse(raw);
    },
    showErrorToast: false,
    enabled: dateReady,
  });

  const { data: technicians, isFetching: techsLoading } = useApiQuery<SchedulerTechnician[]>({
    queryKey: schedulerKeys.technicians(),
    queryFn: () =>
      apiGet<SchedulerTechnician[]>(operationsClient, schedulerEndpoints.technicians()),
    showErrorToast: false,
  });

  // NOT-320 — vocabularies for the Filters popover (crews/event types/job types).
  const { data: crews } = useSchedulerCrews();
  const { data: serviceTypes } = useServiceTypes();
  const { data: jobTypes } = useJobTypes();

  // NOT-317 — jobs without a date/time, for the drag-to-schedule side panel.
  const { bookings: unscheduledBookings } = useUnscheduledBookings();

  const { data: tasksData, isFetching: tasksLoading } = useTasks(
    {
      dueDateFrom: taskDateFrom,
      dueDateTo: taskDateTo,
      limit: 100,
      sortBy: 'dueDate',
      sortOrder: 'asc',
    },
    { enabled: tasksVisible && dateReady, fetchAll: true },
  );

  const bookings = useMemo(() => bookingsRaw?.data ?? [], [bookingsRaw?.data]);
  const scheduledBookings = useMemo(() => bookings.filter(isScheduledBooking), [bookings]);
  // Scheduling v2 §15 — day-bound DAY_FLEXIBLE visits with no time yet (awaiting
  // the route optimizer). Excluded from `scheduledBookings` (a null startAt must
  // never reach FullCalendar), so the Month grid places them by `scheduledDate`
  // as amber "Unsequenced" mini-chips instead.
  const unsequencedBookings = useMemo(
    () =>
      bookings.filter(
        (b) =>
          b.granularity === 'DAY_FLEXIBLE' &&
          !b.startAt &&
          Boolean(b.scheduledDate) &&
          // Terminal rows (mirrors TERMINAL_APPOINTMENT_STATUSES) are done
          // work — never 'awaiting route sequencing'.
          !['CANCELLED', 'CANCELLATION_PENDING', 'COMPLETED', 'SKIPPED', 'EXPIRED'].includes(
            b.status,
          ),
      ),
    [bookings],
  );
  const tasksByDay = useMemo(() => {
    const map: CalendarTasksByDay = new Map();
    if (!tasksVisible) return map;

    for (const task of tasksData?.tasks ?? []) {
      const due = parseTaskDue(task.dueDate);
      if (!due) continue;
      if (isTaskComplete(task.status) || normalizeTaskStatus(task.status) === 'cancelled') continue;
      // The same zone the chip's time is labelled in, or a New York task at
      // 11:30 PM lands on the next day for a viewer in Buenos Aires.
      const key = taskDayKey(task.dueDate, displayTz) ?? toDateString(due.date);
      if (key < dateFrom || key > dateTo) continue;
      const list = map.get(key) ?? [];
      list.push(task);
      map.set(key, list);
    }

    for (const [key, list] of map) {
      map.set(key, sortTasksForCalendar(list));
    }

    return map;
  }, [dateFrom, dateTo, displayTz, tasksData?.tasks, tasksVisible]);
  const activeTechnicians = useMemo(
    () => (technicians ?? []).filter((t) => t.isActive),
    [technicians],
  );
  // NOT-320 — the technician ids implied by the assignee filter, resolving any
  // selected crews to their member techs. Drives the capacity denominator so the
  // count/capacity chip stays in sync when filtering by a crew. Empty = no
  // assignee filter → capacity is computed against all technicians.
  const assigneeTechnicianIds = useMemo(() => {
    if (!filters.assignees.length) return [];
    const ids = new Set<string>();
    for (const t of activeTechnicians) {
      if (filters.assignees.includes(t.id)) ids.add(t.id);
    }
    for (const crew of crews ?? []) {
      if (filters.assignees.includes(crew.id)) {
        for (const t of crew.technicians ?? []) ids.add(t.id);
      }
    }
    return [...ids];
  }, [filters.assignees, activeTechnicians, crews]);

  const isLoading = bookingsLoading || techsLoading || tasksLoading;
  const isToday = currentDate === todayInTz(displayTz);

  // Date display
  const dateDisplay = useMemo(() => {
    // NOT-326 \u2014 Technician spans the same week as Week.
    if (view === 'week' || view === 'technician') {
      const ws = startOfWeek(dateObj, { weekStartsOn });
      const we = endOfWeek(dateObj, { weekStartsOn });
      return `${format(ws, 'MMM d')} \u2013 ${format(we, 'MMM d, yyyy')}`;
    }
    if (view === 'workweek') {
      const ws = startOfWeek(dateObj, { weekStartsOn: workWeekStartsOn });
      const we = addDays(ws, 4);
      return `${format(ws, 'MMM d')} \u2013 ${format(we, 'MMM d, yyyy')}`;
    }
    return format(dateObj, 'MMMM yyyy');
  }, [view, dateObj, weekStartsOn, workWeekStartsOn]);

  const handleBookingClick = useCallback((booking: SchedulerBooking) => {
    setSelectedBooking(booking);
    setDetailOpen(true);
  }, []);

  // Consume a routed visit once, after its range (or unscheduled tray) loads.
  // Keeping the consumed ID prevents a background refetch from reopening a
  // sheet the user has closed; removing the parameter resets navigation.
  const openedRouteBooking = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!search.bookingId) {
      openedRouteBooking.current = undefined;
      return;
    }
    if (openedRouteBooking.current === search.bookingId) return;
    const booking = [...bookings, ...unscheduledBookings].find(
      (candidate) => candidate.bookingId === search.bookingId,
    );
    if (!booking) return;
    openedRouteBooking.current = search.bookingId;
    handleBookingClick(booking);
  }, [search.bookingId, bookings, unscheduledBookings, handleBookingClick]);

  // §15 — series click-highlight: while the detail sheet shows a visit of a
  // multi-visit / recurring job, ring its sibling chips/tiles across the current
  // view. The booking serializer sends no seriesId (src/types/scheduler.ts), so
  // siblings are keyed by the parent jobId — gated to multi-visit or recurring
  // jobs so a plain one-visit job never rings itself. Gate on `detailOpen` (not
  // just `selectedBooking`, which survives close) so the highlight clears with
  // the sheet.
  const selectedSeriesKey = useMemo(() => {
    if (!detailOpen || !selectedBooking?.jobId) return undefined;
    // A real multi-day series rings ONLY its own days (seriesId — a job can
    // hold several series plus unrelated visits); recurring/multi-visit jobs
    // without a seriesId keep the jobId-sibling ring.
    if (selectedBooking.seriesId) return `series:${selectedBooking.seriesId}`;
    const isSeries =
      Boolean(selectedBooking.isRecurring) ||
      (selectedBooking.visitTotal != null && selectedBooking.visitTotal > 1);
    return isSeries ? `job:${selectedBooking.jobId}` : undefined;
  }, [detailOpen, selectedBooking]);

  // The open visit's tile is lifted over the sheet's scrim by class (see
  // `.dispatch-selected` in index.css). FullCalendar owns the event DOM, so the
  // class is toggled on it here rather than through `eventClassNames`, which it
  // only re-runs when the events themselves change.
  useEffect(() => {
    document
      .querySelectorAll('.dispatch-selected')
      .forEach((el) => el.classList.remove('dispatch-selected'));
    const id = detailOpen ? selectedBooking?.bookingId : null;
    if (!id) return;
    document
      .querySelectorAll(`.visit-${id}`)
      .forEach((el) => el.classList.add('dispatch-selected'));
  }, [detailOpen, selectedBooking, view, currentDate]);

  // NOT-319/320 — jobs to plot on the map: the visible range (already scoped by the
  // bookings query) minus cancelled, with the active calendar filters, like the grid.
  // §15 — sourced from the RAW range so day-bound (unsequenced) visits pin too:
  // the map places by coordinates, not startAt.
  const mapBookings = useMemo(
    () => bookings.filter((b) => b.status !== 'CANCELLED' && matchesFilters(b, filters)),
    [bookings, filters],
  );
  // NOT-326 — the Technician grid renders its own grid (no rail inside), so mount
  // the shared rail beside it at the page level. The "No date" group is the
  // unassigned backlog: truly-unassigned (no tech/crew, isUnassignedBooking) +
  // every filter except assignee — so the whole tray is uniformly backlog work,
  // and an assigned-but-undated job never bypasses the page's assignee scope.
  const technicianUnscheduled = useMemo(
    () =>
      unscheduledBookings.filter(
        (b) => isUnassignedBooking(b) && matchesFilters(b, { ...filters, assignees: [] }),
      ),
    [unscheduledBookings, filters],
  );
  // NOT-328 — the Job tray's "No technician" group for the technician rail: dated,
  // truly-unassigned bookings (the in-grid Unassigned row duplicates these, which
  // is accepted — the tray is the single place for "what's left to dispatch").
  // §15 — day-bound (unsequenced) rows are intentionally NOT here: the grid's
  // amber Unsequenced chips carry them, and tray cards drag through the generic
  // schedule/assign paths that route-owned visits must never take.
  const technicianNoTech = useMemo(
    () =>
      scheduledBookings.filter(
        (b) =>
          isUnassignedBooking(b) &&
          b.status !== 'CANCELLED' &&
          matchesFilters(b, { ...filters, assignees: [] }),
      ),
    [scheduledBookings, filters],
  );
  // NOT-318/320 — the Cancelled rail, shared by every desktop view. Sourced from
  // the RAW range (not `scheduledBookings`): a cancelled day-bound visit has no
  // startAt (§15), so the timed-only source would make it vanish from the
  // calendar entirely — the rail is exactly where cancelled work is rescued to.
  const cancelledBookings = useMemo(
    () => selectCancelledBookings(bookings, filters),
    [bookings, filters],
  );
  const mapRangeLabel = useMemo(() => {
    // Format from the date KEY itself (local-built dateObj), never via an ISO
    // instant — converting to UTC would shift the month for viewers in another
    // tz than the business (e.g. Berlin viewing an LA business).
    if (view === 'month') {
      return format(dateObj, 'MMMM yyyy');
    }
    const fmt = (key: string) =>
      formatDateTimeInTimeZone(`${key}T12:00:00Z`, 'UTC', { month: 'short', day: 'numeric' });
    return `${fmt(dateFrom)} – ${fmt(dateTo)}`;
  }, [view, dateObj, dateFrom, dateTo]);

  const handleTaskClick = useCallback(
    (task: Task) => {
      if (task.contactId) {
        void navigate({
          to: '/dashboard/contacts/$contactId',
          params: { contactId: task.contactId },
          search: { ...DEFAULT_CONTACT_LIST_FILTERS, tab: 'summary' as const },
        });
        return;
      }

      if (task.leadId) {
        void navigate({
          to: '/dashboard/leads/$leadId',
          params: { leadId: task.leadId },
          search: { ...DEFAULT_LEAD_LIST_FILTERS, tab: 'tasks' as const },
        });
        return;
      }

      if (task.jobId) {
        void navigate({
          to: '/dashboard/jobs/$id',
          params: { id: task.jobId },
        });
      }
    },
    [navigate],
  );

  const handleCreateBookingSubmit = useCallback(
    async (values: VisitRequestFormValues) => {
      await submitBooking(values);
      closeCreateDrawer();
      // Success toast ("Service request created") comes from useCreateBooking —
      // a second mount-level toast here would double up.
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.all() });
    },
    [closeCreateDrawer, queryClient, submitBooking],
  );

  // ─── Render ─────────────────────────────────────────────────────────────────

  // Gate the surface until the business tz resolves — the grid renders in it and
  // the default day derives from it, so rendering earlier risks the wrong day.
  // Also wait until the fallback view is known (settled settings) when no `?view`
  // is pinned, so we never open on a stale cached default and then flip (NOT-322).
  if (!dateReady || !viewReady) {
    return (
      <ScheduleWorkspaceShell>
        <div className="flex h-full flex-col items-center justify-center gap-3 bg-background">
          {settingsError ? (
            <>
              <p className="text-sm text-muted-foreground">
                Couldn&apos;t load scheduling settings.
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetchSettings()}>
                Retry
              </Button>
            </>
          ) : (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          )}
        </div>
      </ScheduleWorkspaceShell>
    );
  }

  return (
    <ScheduleWorkspaceShell>
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-background">
        {/* Header */}
        <div className="flex shrink-0 flex-col gap-3 border-b bg-background px-3 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center justify-between gap-3 lg:justify-start">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="min-w-0 truncate text-lg font-semibold lg:text-base">{dateDisplay}</h1>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              <span className="shrink-0 text-xs text-muted-foreground">
                {/* §15 — day-bound (unsequenced) visits are visits too: without
                    them the header undercounts a route-heavy range. */}
                · {scheduledBookings.length + unsequencedBookings.length} visit
                {scheduledBookings.length + unsequencedBookings.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-1 lg:order-first">
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 lg:h-8 lg:w-8"
                onClick={goToPrev}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant={isToday ? 'default' : 'outline'}
                size="sm"
                className="h-9 shrink-0 px-3 text-xs lg:h-8"
                onClick={goToToday}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0 lg:h-8 lg:w-8"
                onClick={goToNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between lg:justify-end">
            <ScheduleTimezoneToggle showLabels={false} />
            {/* NOT-320 — multi-select filters + the Tasks toggle, in a right-side
                drawer. List/Board keep their own scoping (and don't use tasks), so
                the drawer is hidden there. */}
            {(view === 'week' ||
              view === 'workweek' ||
              view === 'month' ||
              view === 'technician') && (
              <CalendarFiltersDrawer
                filters={filters}
                onChange={setFilters}
                technicians={activeTechnicians}
                crews={(crews ?? []).filter((c) => c.isActive !== false)}
                serviceTypes={(serviceTypes ?? []).filter((s) => s.isActive !== false)}
                jobTypes={(jobTypes ?? []).filter((j) => j.isActive !== false)}
                tasksEnabled={tasksEnabled}
                onToggleTasks={toggleTasks}
                showTasksToggle={view !== 'technician'}
              />
            )}
            {view === 'week' || view === 'workweek' || view === 'month' || view === 'technician' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 shrink-0 gap-1.5 px-3 text-xs lg:h-8"
                aria-pressed={mapOpen}
                onClick={() => setMapOpen((o) => !o)}
              >
                <MapPinned className="h-3.5 w-3.5" />
                Map
              </Button>
            ) : null}
            {view === 'week' || view === 'workweek' || view === 'month' || view === 'technician' ? (
              // Status colour legend (moved off the old full-width strip to reclaim height).
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0 lg:h-8 lg:w-8"
                    aria-label="Status guide"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-44 p-2">
                  <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">
                    Status guide
                  </p>
                  <div className="flex flex-col gap-1">
                    {(
                      [
                        { status: 'RESERVED', label: 'Reserved' },
                        { status: 'CONFIRMED', label: 'Confirmed' },
                        { status: 'CANCELLATION_PENDING', label: 'Pending cancel' },
                        { status: 'CANCELLED', label: 'Cancelled' },
                      ] as const
                    ).map((item) => (
                      <div key={item.status} className="flex items-center gap-1.5">
                        <span
                          className={cn(
                            'inline-block h-2.5 w-2.5 shrink-0 rounded-full',
                            getStatusDotClass(item.status),
                          )}
                        />
                        <span className="text-[11px] text-muted-foreground">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            ) : null}
            <Tabs value={view} onValueChange={(v) => setView(v as ViewType)}>
              <TabsList className="grid h-9 w-full shrink-0 grid-cols-6 sm:w-auto lg:h-8">
                <TabsTrigger value="week" className="px-3 text-xs">
                  Week
                </TabsTrigger>
                <TabsTrigger value="workweek" className="px-3 text-xs">
                  Work Week
                </TabsTrigger>
                <TabsTrigger value="month" className="px-3 text-xs">
                  Month
                </TabsTrigger>
                <TabsTrigger value="technician" className="px-3 text-xs">
                  Technician
                </TabsTrigger>
                <TabsTrigger value="list" className="px-3 text-xs">
                  List
                </TabsTrigger>
                <TabsTrigger value="board" className="px-3 text-xs">
                  Board
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Main content */}
        <div className="min-h-0 flex-1 overflow-auto bg-muted/20 p-3 lg:hidden">
          <MobileAgendaView
            view={view}
            dateObj={dateObj}
            bookings={scheduledBookings}
            unsequenced={unsequencedBookings}
            tasksByDay={tasksByDay}
            filters={filters}
            onBookingClick={handleBookingClick}
            onTaskClick={handleTaskClick}
          />
        </div>

        <div
          className={cn(
            'hidden min-h-0 flex-1 lg:flex',
            // Releases FullCalendar's stacking contexts while a visit is open,
            // so its tile can lift itself over the sheet's scrim — index.css.
            detailOpen && selectedBooking && 'dispatch-lift-selection',
          )}
        >
          {(view === 'week' || view === 'workweek') && (
            <WeekView
              date={currentDate}
              workWeek={view === 'workweek'}
              bookings={scheduledBookings}
              unsequenced={unsequencedBookings}
              unscheduled={unscheduledBookings}
              cancelled={cancelledBookings}
              tasksByDay={tasksByDay}
              technicians={activeTechnicians}
              filters={filters}
              capacityTechnicianIds={assigneeTechnicianIds}
              seriesKey={selectedSeriesKey}
              selectedBookingId={detailOpen ? selectedBooking?.bookingId : undefined}
              onBookingClick={handleBookingClick}
              onTaskClick={handleTaskClick}
              onAddBooking={openCreateDrawer}
            />
          )}
          {view === 'month' && (
            <MonthView
              dateObj={dateObj}
              bookings={scheduledBookings}
              unsequenced={unsequencedBookings}
              unscheduled={unscheduledBookings}
              cancelled={cancelledBookings}
              tasksByDay={tasksByDay}
              technicians={activeTechnicians}
              filters={filters}
              capacityTechnicianIds={assigneeTechnicianIds}
              seriesKey={selectedSeriesKey}
              selectedBookingId={detailOpen ? selectedBooking?.bookingId : undefined}
              expandedDay={expandedMonthDay}
              onExpandDay={setExpandedMonthDay}
              onBookingClick={handleBookingClick}
              onTaskClick={handleTaskClick}
              onAddBooking={openCreateDrawer}
            />
          )}
          {view === 'technician' && (
            <TechnicianView
              dateObj={dateObj}
              bookings={scheduledBookings}
              unsequenced={unsequencedBookings}
              technicians={activeTechnicians}
              crews={crews ?? []}
              filters={filters}
              weekStartsOn={weekStartsOn}
              seriesKey={selectedSeriesKey}
              // Only highlight while the detail sheet is actually open — closing it
              // flips `detailOpen` but keeps `selectedBooking`, so gating here
              // avoids a stale active ring on the last-clicked card (NOT-327).
              selectedBookingId={detailOpen ? selectedBooking?.bookingId : undefined}
              onBookingClick={handleBookingClick}
              onAddBooking={openCreateDrawer}
              tasksByDay={tasksByDay}
              onTaskClick={handleTaskClick}
            />
          )}
          {view === 'list' && (
            <div className="min-h-0 flex-1 overflow-auto">
              {/* Scope the list to the same range as the header count so they agree.
                  Calendar filters don't apply here (cleared on leaving the calendar
                  views), so the list is unscoped by assignee — never forward a crew
                  id as technicianId (the API treats it strictly as a tech filter). */}
              <BoardListView
                dateFrom={dateFrom}
                dateTo={dateTo}
                openBookingId={detailOpen ? selectedBooking?.bookingId : undefined}
              />
            </div>
          )}
          {view === 'board' && (
            // §5.4 — Kanban lens (jobs by pipeline stage), scoped to the visible
            // range. Drag-to-restage is gated by jobs:edit inside the board.
            <div className="min-h-0 flex-1 overflow-hidden">
              <JobsBoardLens dateFrom={dateFrom} dateTo={dateTo} />
            </div>
          )}
          {/* The Map docks beside the grid (like the Dispatch board) in every
              desktop view instead of a centered modal; the grid reflows and stays
              visible. On mobile it opens as a right-side drawer (below). */}
          {mapOpen && canDockMap && mapSupported && (
            <aside className="relative hidden w-[360px] shrink-0 border-l lg:flex lg:flex-col">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMapOpen(false)}
                aria-label="Close map"
                className="absolute right-1.5 top-1.5 z-10 size-7 bg-background/80 backdrop-blur"
              >
                <X className="size-4" />
              </Button>
              <DispatchMapPanel
                bookings={mapBookings}
                // Deliberate, not a leftover: Calendar has no "Color by" control
                // (its route search carries view/date/tasks/bookingId only), so
                // its grid is unconditionally job-type tinted. The pins follow the
                // grid — `jobtype` resolves through the same `resolveJobTypeHex`
                // chain the tiles use and degrades to the status colour in the
                // same place. Dispatch, which does have the control, passes its
                // live `colorBy` instead.
                colorBy="jobtype"
                selectedBookingId={selectedBooking?.bookingId}
                onSelectBooking={handleBookingClick}
                label={`Map · ${mapRangeLabel}`}
              />
            </aside>
          )}
          {/* NOT-326 — the Technician grid has no internal rail; mount the shared
              Unscheduled/Cancelled rail beside it so no-date jobs stay reachable
              and drag-to-schedule/assign works (cells accept UNSCHEDULED_DND_TYPE). */}
          {view === 'technician' && (
            <CalendarSideRail
              unscheduled={technicianUnscheduled}
              noTech={technicianNoTech}
              cancelled={cancelledBookings}
              businessTz={displayTz}
              dragMode="html5"
              assigneeFiltered={filters.assignees.length > 0}
            />
          )}
        </div>

        {/* NOT-297: one booking detail Sheet (conflict + AI panels) — replaces the
            inline Dialog here and in DispatchBoard. */}
        <BookingDetailSheet
          booking={selectedBooking}
          open={detailOpen}
          onOpenChange={setDetailOpen}
        />
        {/* NOT-319 — map of the visible range's jobs (reuses the Dispatch panel).
            Desktop docks it as a side panel (above); on mobile there's no room to
            dock, so it slides in as a right-side drawer (never a centered modal). */}
        <Sheet open={mapOpen && !canDockMap && mapSupported} onOpenChange={setMapOpen}>
          <SheetContent side="right" className="w-full overflow-hidden p-0 sm:max-w-md">
            <SheetTitle className="sr-only">Jobs map</SheetTitle>
            <DispatchMapPanel
              bookings={mapBookings}
              // Mirrors the docked panel above — job-type pins, status fallback.
              // Same reasoning: Calendar has no colour-mode control to follow.
              colorBy="jobtype"
              selectedBookingId={selectedBooking?.bookingId}
              onSelectBooking={(b) => {
                setMapOpen(false);
                handleBookingClick(b);
              }}
              label={`Map · ${mapRangeLabel}`}
            />
          </SheetContent>
        </Sheet>
        <Sheet
          open={createDrawer.open}
          onOpenChange={(open) => {
            if (!open) closeCreateDrawer();
          }}
        >
          <SheetContent
            side="right"
            showCloseButton={false}
            className="w-full overflow-hidden p-0 sm:max-w-[640px] lg:max-w-[720px]"
          >
            <SheetTitle className="sr-only">New service call</SheetTitle>
            <VisitRequestForm
              mode="drawer"
              title="New service call"
              onCancel={closeCreateDrawer}
              onSubmit={handleCreateBookingSubmit}
              customerMode="select"
              serviceSelectionMode="jobType"
              allowUnscheduled
              // Scheduling v2 — this host submits via POST /bookings, which accepts a
              // planned time with no technician (rendered only when the API is v2).
              allowUnassignedManual
              // Scheduling v2 — all-day requests via POST /bookings; multi-day stays
              // hidden without a job (the form gates it on dayJobId).
              allowDayGranularity
              submitLabel="Create"
              initialDateRange={
                createDrawer.prefill?.date
                  ? { from: createDrawer.prefill.date, to: createDrawer.prefill.date }
                  : undefined
              }
              initialTechnicianId={createDrawer.prefill?.technicianId}
              initialStartAt={createDrawer.prefill?.startAt}
              onReserveSlot={reserveBookingSlot}
            />
          </SheetContent>
        </Sheet>
      </div>
    </ScheduleWorkspaceShell>
  );
}

function TaskChipIcon({ task }: { task: Task }) {
  const Icon = task.type && task.type !== 'TODO' ? taskTypeOption(task.type).icon : Circle;
  return <Icon className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />;
}

function CalendarTaskChip({
  task,
  onClick,
  compact = false,
}: {
  task: Task;
  onClick: () => void;
  compact?: boolean;
}) {
  const overdue = isTaskOverdue({ dueDate: task.dueDate, status: task.status });
  const assignee = getTaskAssigneeName(task);
  const timeZone = useScheduleTimezone().displayTimezone;
  const label = formatCalendarTaskLabel(task, timeZone);

  return (
    <button
      type="button"
      title={assignee ? `${label} · ${assignee}` : label}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={cn(
        'inline-flex min-w-0 items-center gap-1 rounded border px-1.5 py-0.5 text-left text-[11px] leading-4 transition-colors hover:bg-muted',
        overdue
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300'
          : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300',
        compact ? 'max-w-full' : 'max-w-[260px]',
      )}
    >
      <TaskChipIcon task={task} />
      <span className="truncate">{label}</span>
    </button>
  );
}

function CalendarTaskCard({ task, onClick }: { task: Task; onClick: () => void }) {
  const overdue = isTaskOverdue({ dueDate: task.dueDate, status: task.status });
  const assignee = getTaskAssigneeName(task);
  const timeZone = useScheduleTimezone().displayTimezone;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border p-3 text-left transition-colors hover:bg-muted/70',
        overdue
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300'
          : 'border-amber-200 bg-amber-50/70 text-amber-900 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300',
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Circle className="h-3.5 w-3.5 shrink-0" />
        <p className="min-w-0 truncate text-sm font-medium">{task.title}</p>
      </div>
      <p className="mt-1 text-xs">{formatCalendarTaskLabel(task, timeZone)}</p>
      {assignee ? <p className="mt-1 truncate text-xs opacity-80">{assignee}</p> : null}
    </button>
  );
}

function WeekView({
  date,
  workWeek = false,
  bookings,
  unsequenced,
  unscheduled,
  cancelled,
  tasksByDay,
  technicians,
  filters,
  capacityTechnicianIds,
  seriesKey,
  selectedBookingId,
  onBookingClick,
  onTaskClick,
  onAddBooking,
}: {
  /** Route day as `YYYY-MM-DD` — date-only so FullCalendar never tz-shifts it. */
  date: string;
  /** NOT-323 — render the 5-day Work Week (from workWeekStartDay) instead of 7. */
  workWeek?: boolean;
  bookings: ScheduledBooking[];
  /** §15 — day-bound DAY_FLEXIBLE visits with no time yet; all-day lane rows. */
  unsequenced: SchedulerBooking[];
  unscheduled: SchedulerBooking[];
  /** NOT-318 + §15 — rail rows from the RAW range (timed + day-bound cancelled). */
  cancelled: SchedulerBooking[];
  tasksByDay: CalendarTasksByDay;
  technicians: SchedulerTechnician[];
  filters: CalendarFilters;
  /** NOT-320 — technician ids implied by the assignee filter (crews resolved to
   *  members); scopes the capacity denominator. Empty = no filter → all techs. */
  capacityTechnicianIds: string[];
  /** §15 — the open sheet's series key (parent jobId); rings sibling events. */
  seriesKey?: string;
  /** The visit whose sheet is open: lifted clear of the sheet's scrim. */
  selectedBookingId?: string;
  onBookingClick: (b: SchedulerBooking) => void;
  onTaskClick: (task: Task) => void;
  onAddBooking: (prefill: CreateBookingPrefill) => void;
}) {
  // Same Trade-palette resolver the map pins use, so a tile and its pin can
  // never disagree about a booking with no persisted job-type colour.
  const resolveJobTypeFamily = useJobTypeFamilyResolver();
  const { data: settings } = useBusinessSettings();
  const tenantTz = settings?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const scheduleDisplay = useScheduleTimezone();
  const businessTz = scheduleDisplay.displayTimezone;
  // NOT-323 — Work Week starts on its own configured day and shows 5 days.
  const firstDay = asWeekStart(workWeek ? settings?.workWeekStartDay : settings?.weekStartDay);
  // Visible window from tenant business hours (±1h padding), like DispatchBoard.
  // Widened below to also cover any out-of-hours timed job (NOT-321).
  const baseMinTime =
    scheduleDisplay.mode === 'browser'
      ? '00:00:00'
      : paddedBound(settings?.businessHoursStart || FALLBACK_OPEN, -1);
  const baseMaxTime =
    scheduleDisplay.mode === 'browser'
      ? '24:00:00'
      : paddedBound(settings?.businessHoursEnd || FALLBACK_CLOSE, 1);

  const techMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of technicians) m.set(t.id, t.name);
    return m;
  }, [technicians]);

  // Capacity denominator = the techs in view. When the assignee filter names
  // technicians (or crews, resolved to their members), scope to those; otherwise
  // use all. Keeps the count/capacity chip in sync with the filtered bookings.
  const techsInView = useMemo(() => {
    const picked = technicians.filter((t) => capacityTechnicianIds.includes(t.id));
    return picked.length ? picked : technicians;
  }, [technicians, capacityTechnicianIds]);

  // NOT-330 — full-day time-off / non-working techs per day, scoped to the techs
  // in view. The Week/Month grids are aggregates: with one tech in view we name
  // the reason; with several we show a compact "N off" pill (names in the title).
  const offForDay = useCallback(
    (key: string) =>
      techsInView
        .map((t) => ({ tech: t, off: techDayUnavailability(t, parseLocalDate(key), tenantTz) }))
        .filter((x): x is { tech: SchedulerTechnician; off: { label: string; tooltip: string } } =>
          Boolean(x.off),
        ),
    [techsInView, tenantTz],
  );

  const filteredBookings = useMemo(
    () => bookings.filter((b) => b.status !== 'CANCELLED' && matchesFilters(b, filters)),
    [bookings, filters],
  );

  // NOT-317/320/328 — the Job tray (No date + No technician). Filtered by
  // status/event/job-type/skills but NOT assignee — tray items have no (relevant)
  // technician, so an assignee filter would empty them.
  // §15 — day-bound (unsequenced) rows are intentionally NOT in the tray: the
  // amber all-day lane rows carry them, and tray cards drag through the generic
  // /schedule path that route-owned visits must never take.
  const trayFilters = useMemo(() => ({ ...filters, assignees: [] }), [filters]);
  const filteredUnscheduled = useMemo(
    () => unscheduled.filter((b) => isUnassignedBooking(b) && matchesFilters(b, trayFilters)),
    [unscheduled, trayFilters],
  );
  const noTechBookings = useMemo(
    () =>
      bookings.filter(
        (b) => isUnassignedBooking(b) && b.status !== 'CANCELLED' && matchesFilters(b, trayFilters),
      ),
    [bookings, trayFilters],
  );

  // NOT-321 — FullCalendar silently drops timed events outside [slotMin, slotMax].
  // Widen the window so the earliest/latest TIMED job is always inside it (all-day
  // & multi-day go to the all-day row, so they're excluded from the bound).
  const { slotMinTime, slotMaxTime } = useMemo(() => {
    let earliest: string | null = null;
    let latest: string | null = null;
    for (const b of filteredBookings) {
      if (!b.startAt) continue;
      const multiDay =
        !!b.endAt &&
        getBookingDayKey(b.startAt, businessTz) !== getBookingDayKey(b.endAt, businessTz);
      if (b.granularity === 'ALL_DAY' || multiDay) continue;
      const start = wallTimeInTz(b.startAt, businessTz);
      const end = b.endAt ? wallTimeInTz(b.endAt, businessTz) : start;
      if (!earliest || start < earliest) earliest = start;
      if (!latest || end > latest) latest = end;
    }
    return {
      slotMinTime:
        earliest && earliest < baseMinTime ? roundToHalfHour(earliest, 'floor') : baseMinTime,
      slotMaxTime: latest && latest > baseMaxTime ? roundToHalfHour(latest, 'ceil') : baseMaxTime,
    };
  }, [filteredBookings, businessTz, baseMinTime, baseMaxTime]);

  // Key by the business-tz day so the per-column header count matches the grid
  // columns (which render in businessTz), regardless of the viewer's local zone.
  // §15 — day-bound rows per scheduledDate for the header capacity pill.
  const unsequencedCountByDay = useMemo(() => {
    const map = new Map<string, number>();
    for (const b of unsequenced) {
      if (!b.scheduledDate || !matchesFilters(b, filters)) continue;
      map.set(b.scheduledDate, (map.get(b.scheduledDate) ?? 0) + 1);
    }
    return map;
  }, [unsequenced, filters]);

  const bookingsByDay = useMemo(() => {
    const map = new Map<string, ScheduledBooking[]>();
    for (const b of filteredBookings) {
      const startKey = getBookingDayKey(b.startAt, businessTz);
      if (!startKey) continue;
      // Count the booking on every day it covers so multi-day spans aren't
      // undercounted on days after their start (NOT-316). Single-day → one entry.
      const endKey = b.endAt ? getBookingDayKey(b.endAt, businessTz) : startKey;
      let key = startKey;
      while (key && key <= endKey) {
        const list = map.get(key) ?? [];
        list.push(b);
        map.set(key, list);
        if (key === endKey) break;
        key = nextDayKey(key);
      }
    }
    return map;
  }, [filteredBookings, businessTz]);

  // Bookings placed by their hour (height = duration; null end → FC assumes 1h).
  const events = useMemo<EventInput[]>(
    () =>
      filteredBookings.map((b) => {
        // NOT-316 — all-day and multi-day jobs belong in the fixed "All day" row,
        // not the hourly grid. Multi-day is derived from start/end spanning days.
        const startKey = getBookingDayKey(b.startAt, businessTz);
        const endKey = b.endAt ? getBookingDayKey(b.endAt, businessTz) : startKey;
        const multiDay = endKey !== startKey;
        // FullCalendar's own `allDay` event prop, sourced from granularity
        // (the DB `allDay` column was dropped — Scheduling v2). A TIMED visit that
        // merely crosses midnight (e.g. 11:20pm–00:20) is NOT all-day — it must stay
        // on the hourly grid. Only genuine ALL_DAY visits, or a non-timed span across
        // days, belong in the fixed All-day row.
        const allDay = b.granularity === 'ALL_DAY' || (multiDay && b.granularity !== 'TIMED');
        if (allDay) {
          // FC all-day end is day-exclusive, so a job covering its last day needs
          // end = lastDay + 1. Use date-only strings so it spans whole columns.
          return {
            id: b.bookingId,
            start: startKey,
            end: nextDayKey(endKey),
            allDay: true,
            // Scheduling v2 — a day-bound (ALL_DAY) visit moves by DAY via the
            // visit day actions, never the generic /move drag (the backend keys
            // its day-scoped reads on scheduledDate, which /move never rewrites).
            editable: b.granularity !== 'ALL_DAY',
            title: b.customerName,
            extendedProps: { booking: b },
            ...bookingJobTypeStyle(b, resolveJobTypeFamily),
          };
        }
        return {
          id: b.bookingId,
          start: b.startAt,
          end: b.endAt ?? undefined,
          allDay: false,
          title: b.customerName,
          // §8/§15 — optimized route times are route-owned: no drag/resize
          // through the generic move path (route moves land next phase).
          editable: b.granularity !== 'DAY_FLEXIBLE',
          extendedProps: { booking: b },
          ...bookingJobTypeStyle(b, resolveJobTypeFamily),
        };
      }),
    [filteredBookings, businessTz, resolveJobTypeFamily],
  );

  // Tasks as all-day events (the all-day row only shows when there are tasks).
  const taskEvents = useMemo<EventInput[]>(() => {
    const out: EventInput[] = [];
    for (const [dayKey, tasks] of tasksByDay) {
      for (const t of tasks) {
        const block = taskTimeBlock(t);
        out.push({
          id: `task-${t.id}`,
          ...(block ? { ...block, allDay: false } : { start: dayKey, allDay: true }),
          title: t.title,
          // NOT-325 — tasks aren't reschedulable by drag (jobs only); opt them out
          // of the grid's global eventStartEditable.
          startEditable: false,
          durationEditable: false,
          extendedProps: { task: t },
        });
      }
    }
    return out;
  }, [tasksByDay]);

  // §15 — unsequenced DAY_FLEXIBLE visits (scheduledDate, no time yet) as
  // all-day lane rows, so day-bound work isn't invisible on the time grids.
  // Placed by their tenant-local scheduledDate (a date-only string, so FC never
  // tz-shifts it); never draggable — route moves go via the route actions.
  const unsequencedEvents = useMemo<EventInput[]>(
    () =>
      unsequenced
        .filter((b) => matchesFilters(b, filters))
        .map((b) => ({
          id: b.bookingId,
          start: b.scheduledDate as string,
          allDay: true,
          title: b.customerName,
          editable: false,
          extendedProps: { booking: b, unsequenced: true },
          ...bookingJobTypeStyle(b, resolveJobTypeFamily),
        })),
    [unsequenced, filters, resolveJobTypeFamily],
  );

  // `initialDate` is only read on mount; follow the route date (prev/next/today)
  // afterward by navigating the calendar imperatively (mirrors DispatchBoard).
  const calendarRef = useRef<FullCalendar>(null);
  useEffect(() => {
    calendarRef.current?.getApi().gotoDate(date);
  }, [date]);

  // NOT-323 — `initialView` only applies on mount; switch the live instance when
  // toggling between the 7-day Week and the 5-day Work Week.
  const fcView = workWeek ? 'timeGridWorkWeek' : 'timeGridWeek';
  useEffect(() => {
    calendarRef.current?.getApi().changeView(fcView);
  }, [fcView]);

  // NOT-317 — make the unscheduled panel cards draggable into the FC grid. The
  // panel owns the DOM; FC's Draggable reads each card's `data-*` to build the
  // dropped event. A CALLBACK ref (not useEffect+useRef) re-instantiates the
  // Draggable whenever the container mounts/unmounts — so collapsing then
  // reopening the panel (a fresh DOM node) doesn't silently break the drag.
  const scheduleBooking = useScheduleBooking();
  const moveBooking = useMoveBooking();
  // 409 conflict → "This move has a conflict" / "Move anyway" → retry with
  // overrideConflicts; cancel reverts the drag. Non-409 errors revert + toast.
  const { run: runWithOverride, dialog: overrideDialog } = useConflictOverride({
    allowArrivalWindowChange: true,
  });
  const techNameById = useMemo(
    () => new Map(technicians.map((t) => [t.id, t.name] as const)),
    [technicians],
  );
  const involvedTechs = useCallback(
    (technicianId: string | null | undefined) =>
      technicianId
        ? [{ id: technicianId, name: techNameById.get(technicianId) ?? 'Technician' }]
        : [],
    [techNameById],
  );
  const draggableRef = useRef<Draggable | null>(null);
  const panelRef = useCallback((node: HTMLDivElement | null) => {
    draggableRef.current?.destroy();
    draggableRef.current = null;
    if (node) {
      draggableRef.current = new Draggable(node, {
        itemSelector: '.cal-unscheduled-card',
        eventData: (el) => ({
          title: (el as HTMLElement).dataset.title ?? '',
          duration: { minutes: Number((el as HTMLElement).dataset.durationMin) || 60 },
          extendedProps: {
            bookingId: (el as HTMLElement).dataset.bookingId,
            // Present only on scheduled "No technician" tray cards — lets
            // eventReceive no-op a same-day all-day drop (NOT-328).
            fromDayKey: (el as HTMLElement).dataset.fromDayKey,
          },
        }),
      });
    }
  }, []);

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden bg-card">
      <div className="min-h-0 flex-1 overflow-hidden">
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin, luxonPlugin]}
          droppable
          eventReceive={(arg) => {
            // The temporary event FC creates on drop is discarded — the schedule
            // mutation + query invalidation re-render the real booking. Calendar
            // scheduling sets the time only; the tech is assigned later in Dispatch.
            const bookingId = arg.event.extendedProps.bookingId as string | undefined;
            arg.revert();
            if (!bookingId || !arg.event.start) return;
            // A scheduled "No technician" tray card dropped back on its own day's
            // all-day lane would otherwise be re-scheduled to the open hour (e.g.
            // 2:30 PM → 9:00 AM). No-op that same-day all-day case (NOT-328).
            const fromDayKey = arg.event.extendedProps.fromDayKey as string | undefined;
            if (
              fromDayKey &&
              arg.event.allDay &&
              fromDayKey === getBookingDayKey(arg.event.start.toISOString(), businessTz)
            ) {
              return;
            }
            // A drop on the all-day lane has no time → would persist midnight and
            // hide above the business-hours grid. Anchor it to the business open
            // hour that day (mirrors Month), otherwise use the exact slot instant.
            const startAt = arg.event.allDay
              ? zonedDateTimeToUtc(
                  parseLocalDate(getBookingDayKey(arg.event.start.toISOString(), businessTz)),
                  (settings?.businessHoursStart || '09:00').slice(0, 5),
                  tenantTz,
                )?.toISOString()
              : arg.event.start.toISOString();
            if (!startAt) return;
            void runWithOverride(
              (overrideConflicts, arrivalWindowChange, notification) =>
                scheduleBooking.mutateAsync({
                  id: bookingId,
                  startAt,
                  overrideConflicts,
                  manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
                  arrivalWindowChange,
                  notification,
                }),
              {
                appointmentId: bookingId,
                title: 'This move has a conflict',
                confirmLabel: 'Move anyway',
              },
            ).catch((err: Error) => toast.error(err.message || 'Could not schedule'));
          }}
          // NOT-325 — drag an existing job to another time/day to reschedule it
          // (keeping its tech). Move only, no resize — durations stay fixed.
          eventStartEditable={true}
          // NOT-329 — resize the bottom/top edge to change duration; persisted via
          // the new endAt (see eventResize). Tasks opt out (startEditable:false).
          eventDurationEditable={true}
          eventResize={(arg) => {
            const booking = arg.event.extendedProps.booking as ScheduledBooking | undefined;
            const bookingId = booking?.bookingId;
            // Jobs only; all-day has no time axis → revert.
            if (!booking || !bookingId || !arg.event.start || !arg.event.end || arg.event.allDay) {
              arg.revert();
              return;
            }
            const startAt = arg.event.start.toISOString();
            const endAt = arg.event.end.toISOString();
            const technicianId = booking.technicianId;
            void runWithOverride(
              (overrideConflicts, arrivalWindowChange, notification) =>
                technicianId
                  ? moveBooking.mutateAsync({
                      id: bookingId,
                      dto: {
                        technicianId,
                        startAt,
                        endAt,
                        overrideConflicts,
                        manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
                        arrivalWindowChange,
                        notification,
                      },
                    })
                  : scheduleBooking.mutateAsync({
                      id: bookingId,
                      startAt,
                      endAt,
                      overrideConflicts,
                      manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
                      arrivalWindowChange,
                      notification,
                    }),
              {
                appointmentId: bookingId,
                title: 'This move has a conflict',
                confirmLabel: 'Move anyway',
                technicians: involvedTechs(technicianId),
                onDeclined: () => arg.revert(),
              },
            )
              .then((saved) => {
                if (saved) toast.success('Duration updated');
              })
              .catch((err: Error) => {
                arg.revert();
                toast.error(err.message || 'Could not resize');
              });
          }}
          eventDrop={(arg) => {
            const booking = arg.event.extendedProps.booking as ScheduledBooking | undefined;
            const bookingId = booking?.bookingId;
            // Only jobs reschedule by drag; tasks revert (they set startEditable:false
            // so this shouldn't fire, but guard anyway).
            if (!booking || !bookingId || !arg.event.start) {
              arg.revert();
              return;
            }
            // Day-bound (ALL_DAY) visits never move through the generic /move
            // path (their chips set editable:false, but guard anyway).
            if (booking.granularity === 'ALL_DAY') {
              toast.error('Day-bound visits move via the visit day actions.');
              arg.revert();
              return;
            }
            // §8/§15 — route-owned times never move via generic drag.
            if (booking.granularity === 'DAY_FLEXIBLE') {
              toast.error('Route visits are optimizer-owned — use the route actions to move them.');
              arg.revert();
              return;
            }
            // An all-day-lane drop on the SAME day would reset the time to the
            // business open hour even though the day never changed (e.g. a 2:30pm
            // visit nudged onto the all-day row). No-op that case.
            const targetDayKey = getBookingDayKey(arg.event.start.toISOString(), businessTz);
            if (
              arg.event.allDay &&
              booking.startAt &&
              getBookingDayKey(booking.startAt, businessTz) === targetDayKey
            ) {
              arg.revert();
              return;
            }
            // A drop on the all-day lane has no time → anchor to the business open
            // hour that day (mirrors eventReceive), otherwise use the slot instant.
            const startAt = arg.event.allDay
              ? zonedDateTimeToUtc(
                  parseLocalDate(targetDayKey),
                  (settings?.businessHoursStart || '09:00').slice(0, 5),
                  tenantTz,
                )?.toISOString()
              : arg.event.start.toISOString();
            if (!startAt) {
              arg.revert();
              return;
            }
            // Assigned → /move (keeps tech, checks conflicts); unassigned → /schedule
            // (time-only, avoids the 422 that /move throws without a tech).
            const technicianId = booking.technicianId;
            void runWithOverride(
              (overrideConflicts, arrivalWindowChange, notification) =>
                technicianId
                  ? moveBooking.mutateAsync({
                      id: bookingId,
                      dto: {
                        technicianId,
                        startAt,
                        overrideConflicts,
                        manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
                        arrivalWindowChange,
                        notification,
                      },
                    })
                  : scheduleBooking.mutateAsync({
                      id: bookingId,
                      startAt,
                      overrideConflicts,
                      manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
                      arrivalWindowChange,
                      notification,
                    }),
              {
                appointmentId: bookingId,
                title: 'This move has a conflict',
                confirmLabel: 'Move anyway',
                technicians: involvedTechs(technicianId),
                onDeclined: () => arg.revert(),
              },
            )
              .then((saved) => {
                if (saved && technicianId) toast.success('Booking rescheduled');
              })
              .catch((err: Error) => {
                arg.revert();
                toast.error(err.message || 'Could not reschedule');
              });
          }}
          views={{
            // NOT-323 — 5 consecutive days aligned to firstDay (the work-week start).
            timeGridWorkWeek: { type: 'timeGrid', duration: { days: 5 }, dateAlignment: 'week' },
          }}
          initialView={fcView}
          initialDate={date}
          timeZone={businessTz}
          firstDay={firstDay}
          headerToolbar={false}
          allDaySlot={true}
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          nowIndicator
          height="100%"
          expandRows
          selectable
          events={[...events, ...taskEvents, ...unsequencedEvents]}
          dayHeaderContent={(arg: DayHeaderContentArg) => {
            // With the luxon plugin FC's `date` is a real instant (not a wall-time
            // marker), so neither local nor UTC accessors are safe. Derive the
            // column's day/label in businessTz via the shared helper, and trust FC's
            // own `isToday` (tz-aware) — keeps key, label and count on the same bucket.
            const key = getBookingDayKey(arg.date.toISOString(), businessTz);
            // §15 — unsequenced day-bound visits consume capacity like timed
            // ones; the pill must count them or a route day reads as free.
            const count =
              (bookingsByDay.get(key)?.length ?? 0) + (unsequencedCountByDay.get(key) ?? 0);
            const weekday = formatDateTimeInTimeZone(arg.date.toISOString(), businessTz, {
              weekday: 'short',
            });
            const dayNum = formatDateTimeInTimeZone(arg.date.toISOString(), businessTz, {
              day: 'numeric',
            });
            const off = offForDay(key);
            const single = techsInView.length === 1;
            // Capacity excludes techs who are fully off that day, so the pill never
            // contradicts the "N off" indicator (a tech on PTO adds no capacity).
            const offIds = new Set(off.map((o) => o.tech.id));
            const dayCap = totalDayCapacity(techsInView.filter((t) => !offIds.has(t.id)));
            return (
              <div className="py-1 text-center">
                <div className="text-[11px] text-muted-foreground">{weekday}</div>
                <div className={cn('text-sm font-semibold', arg.isToday && 'text-primary')}>
                  {dayNum}
                </div>
                {dayCap > 0 ? (
                  <span
                    className={cn(
                      'mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                      capacityChipClasses(count, dayCap),
                    )}
                  >
                    {count}/{dayCap}
                  </span>
                ) : null}
                {off.length > 0 ? (
                  <span
                    className="mt-0.5 flex items-center justify-center gap-1 text-[10px] text-muted-foreground"
                    title={
                      single
                        ? off[0].off.tooltip
                        : off.map((o) => `${o.tech.name}: ${o.off.label}`).join('\n')
                    }
                  >
                    <Ban className="size-2.5" />
                    {single ? off[0].off.label : `${off.length} off`}
                  </span>
                ) : null}
              </div>
            );
          }}
          eventClassNames={(arg: EventContentArg) => {
            const booking = arg.event.extendedProps.booking as SchedulerBooking | undefined;
            if (!booking) return ['cal-task-event'];
            // §15 — ring every sibling of the series open in the detail sheet.
            const seriesRing = isSeriesSibling(booking, seriesKey)
              ? ['ring-2', 'ring-primary/60']
              : [];
            // A stable per-visit class, rendered once with the event. The open
            // visit's ring is then toggled on it from the page (see the effect
            // by `selectedBooking`) — FullCalendar re-runs this only when its
            // event data changes, so a selection alone would never reach it.
            seriesRing.push(`visit-${booking.bookingId}`);
            const hasJobTypeColor = Boolean(resolveJobTypeHex(booking, resolveJobTypeFamily));
            if (arg.event.extendedProps.unsequenced) {
              return [
                'dispatch-block',
                ...(hasJobTypeColor
                  ? ['text-foreground']
                  : [
                      'border-amber-200',
                      'bg-amber-50',
                      'text-amber-800',
                      'dark:border-amber-900',
                      'dark:bg-amber-950/50',
                      'dark:text-amber-300',
                    ]),
                ...(isPendingWeatherHold(booking) ? ['cal-hatch'] : []),
                ...seriesRing,
              ];
            }
            return [
              'dispatch-block',
              ...(hasJobTypeColor ? [] : getStatusBlockTint(booking.status).split(' ')),
              ...getStatusBarClass(booking.status).split(' '),
              ...(isPendingWeatherHold(booking) ? ['cal-hatch'] : []),
              ...seriesRing,
            ];
          }}
          eventContent={(arg: EventContentArg) => {
            const booking = arg.event.extendedProps.booking as SchedulerBooking | undefined;
            if (!booking) {
              const task = arg.event.extendedProps.task as Task | undefined;
              const TaskIcon = task ? taskTypeOption(task.type).icon : SquareCheck;
              return (
                <span
                  className={cn(
                    'truncate px-1 text-[11px]',
                    !arg.event.allDay && 'block py-0.5 leading-4',
                  )}
                >
                  <TaskIcon className="mr-1 inline size-3" aria-hidden="true" />
                  {!arg.event.allDay && arg.timeText ? (
                    <span className="mr-1 font-semibold">{arg.timeText}</span>
                  ) : null}
                  {arg.event.title}
                </span>
              );
            }
            // §15 — time-pending day rows mirror the Month chip's one-liner
            // (colors come from eventClassNames on the event element).
            if (arg.event.extendedProps.unsequenced) {
              return (
                <div
                  className="relative flex min-w-0 items-center gap-1.5 px-1.5 py-0.5 text-[11px] leading-4"
                  title={`${tileTitle(booking)} — All Day`}
                >
                  <JobTypeIcon icon={booking.jobType?.icon} />
                  <span className="truncate font-medium">{tileTitle(booking)}</span>
                  <JobTagChips tags={booking.tags} max={2} compact />
                  <span className="ml-auto shrink-0 text-[9px] font-semibold uppercase tracking-wide">
                    All Day
                  </span>
                </div>
              );
            }
            const time = formatDateTimeInTimeZone(booking.startAt, businessTz, {
              hour: 'numeric',
              minute: '2-digit',
            });
            const tech = booking.technicianId
              ? (techMap.get(booking.technicianId) ?? booking.technicianName ?? 'Assigned')
              : 'Unassigned';
            const visit = visitLabel(booking);
            const live = liveStatusLabel(booking.status);
            // NOT-329 — the harness pins the tile to its duration-derived height,
            // so at 30 min or less the 3-line body would paint over the slot below.
            // Collapse to the identity line + time rather than clip three of them.
            if (isShortTile(arg.event.start, arg.event.end)) {
              return (
                <JobHoverCard booking={booking}>
                  <div
                    className="relative flex h-full min-w-0 items-center gap-1 overflow-hidden px-1.5 leading-none"
                    title={`${tileTitle(booking)} — ${live ?? time}`}
                  >
                    {booking.technicianId ? (
                      <span className="flex size-3.5 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-semibold">
                        {nameInitials(tech)}
                      </span>
                    ) : null}
                    <CompletedTileCheck status={booking.status} />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-foreground">
                      {tileTitle(booking)}
                    </span>
                    <JobTagChips tags={booking.tags} max={2} compact />
                    {/* The time yields (min-w-0) before the badges do — in a narrow
                      week column the badge stack must stay whole, not slide out. */}
                    <span className="flex min-w-0 items-center gap-1 overflow-hidden text-[10px] text-muted-foreground">
                      {live ? (
                        <>
                          <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500" />
                          <span className="font-medium text-foreground">{live}</span>
                        </>
                      ) : (
                        time
                      )}
                    </span>
                    {/* NOT-329 — status badges (every mode). In flow, never absolute:
                      the stack is 1..4 pills wide, so any reserved padding is wrong
                      for some booking and the badges land on top of the text. */}
                    <TileBadges booking={booking} className="shrink-0" />
                  </div>
                </JobHoverCard>
              );
            }
            return (
              <JobHoverCard booking={booking}>
                <div className="flex min-w-0 gap-1 overflow-hidden px-1.5 py-0.5">
                  <div className="flex min-w-0 flex-1 flex-col gap-[1px]">
                    {/* Line 1: tech avatar + customer (no tech-row here → show whose job). */}
                    <span className="flex min-w-0 items-center gap-1 text-[11px] font-medium leading-tight text-foreground">
                      {booking.technicianId ? (
                        <span className="flex size-3.5 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-semibold">
                          {nameInitials(tech)}
                        </span>
                      ) : null}
                      <CompletedTileCheck status={booking.status} />
                      <span className="truncate">{tileTitle(booking)}</span>
                    </span>
                    {/* Tags on their own line so they never truncate the name above. */}
                    <JobTagChips tags={booking.tags} max={3} className="flex-wrap" />
                    {/* Line 2: job type · visit label. */}
                    <span className="flex min-w-0 items-center gap-1 text-[10px] leading-tight text-muted-foreground">
                      <JobTypeIcon icon={booking.jobType?.icon} />
                      <span className="truncate">
                        {booking.serviceType?.name ?? 'Service'}
                        {visit ? ` · ${visit}` : ''}
                      </span>
                    </span>
                    {/* Line 3: time (or live status word) — All-day rows skip it. */}
                    {arg.event.allDay ? null : (
                      <span className="flex items-center gap-1 truncate text-[10px] leading-tight text-muted-foreground">
                        {live ? (
                          <>
                            <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500" />
                            <span className="font-medium text-foreground">{live}</span>
                          </>
                        ) : (
                          time
                        )}
                      </span>
                    )}
                  </div>
                  {/* NOT-329 — status badges top-right (every mode). In flow beside the
                    text column, never absolute — see the short-tile branch above. */}
                  <TileBadges booking={booking} className="shrink-0 self-start" />
                </div>
              </JobHoverCard>
            );
          }}
          eventClick={(arg: EventClickArg) => {
            const booking = arg.event.extendedProps.booking as SchedulerBooking | undefined;
            if (booking) {
              onBookingClick(booking);
              return;
            }
            const task = arg.event.extendedProps.task as Task | undefined;
            if (task) onTaskClick(task);
          }}
          select={(arg: DateSelectArg) =>
            // Use FullCalendar's tz-aware strings (business zone), not the browser-local
            // Date, so the prefilled day/time matches the slot the dispatcher clicked.
            // All-day selections carry no meaningful time → prefill the day only,
            // never an unintended timed slot (NOT-316).
            onAddBooking(
              arg.allDay
                ? { date: arg.startStr.slice(0, 10) }
                : { date: arg.startStr.slice(0, 10), startAt: arg.startStr },
            )
          }
        />
      </div>
      <CalendarSideRail
        ref={panelRef}
        unscheduled={filteredUnscheduled}
        noTech={noTechBookings}
        cancelled={cancelled}
        businessTz={businessTz}
        dragMode="fc"
        assigneeFiltered={filters.assignees.length > 0}
      />
      {overrideDialog}
    </div>
  );
}

function MonthView({
  dateObj,
  bookings,
  unsequenced,
  unscheduled,
  cancelled,
  tasksByDay,
  technicians,
  filters,
  capacityTechnicianIds,
  seriesKey,
  selectedBookingId,
  expandedDay,
  onExpandDay,
  onBookingClick,
  onTaskClick,
  onAddBooking,
}: {
  dateObj: Date;
  bookings: ScheduledBooking[];
  /** §15 — day-bound DAY_FLEXIBLE visits with no time yet; placed by scheduledDate. */
  unsequenced: SchedulerBooking[];
  unscheduled: SchedulerBooking[];
  /** NOT-318 + §15 — rail rows from the RAW range (timed + day-bound cancelled). */
  cancelled: SchedulerBooking[];
  tasksByDay: CalendarTasksByDay;
  technicians: SchedulerTechnician[];
  filters: CalendarFilters;
  /** NOT-320 — technician ids implied by the assignee filter (crews resolved to
   *  members); scopes the capacity denominator. Empty = no filter → all techs. */
  capacityTechnicianIds: string[];
  /** §15 — the open sheet's series key (parent jobId); rings sibling chips. */
  seriesKey?: string;
  /** The visit whose sheet is open: lifted clear of the sheet's scrim. */
  selectedBookingId?: string;
  expandedDay: string | null;
  onExpandDay: (day: string | null) => void;
  onBookingClick: (b: SchedulerBooking) => void;
  onTaskClick: (task: Task) => void;
  onAddBooking: (prefill: CreateBookingPrefill) => void;
}) {
  // Same Trade-palette resolver the map pins use, so a tile and its pin can
  // never disagree about a booking with no persisted job-type colour.
  const resolveJobTypeFamily = useJobTypeFamilyResolver();
  const { data: monthSettings } = useBusinessSettings();
  const monthTenantTz = monthSettings?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const monthScheduleTz = useScheduleTimezone();
  // Page-wide zone: correct for chrome (the "today" marker, day headers) that
  // belongs to no single booking. Per-booking times go through `monthZoneFor`.
  const monthBusinessTz = monthScheduleTz.displayTimezone;
  const monthZoneFor = useCallback(
    (booking: { siteTimezone?: string | null }) => monthScheduleTz.zoneFor(booking.siteTimezone),
    [monthScheduleTz],
  );
  // NOT-322 — the month grid starts its rows on the tenant's week-start too.
  const weekStartsOn = asWeekStart(monthSettings?.weekStartDay);
  const scheduleBooking = useScheduleBooking();
  const moveBooking = useMoveBooking();
  const { run: runWithOverride, dialog: overrideDialog } = useConflictOverride({
    allowArrivalWindowChange: true,
  });
  const [dragOverDay, setDragOverDay] = useState<string | null>(null);

  // NOT-317/325 — place a booking on a Month day cell. The grid has no time axis,
  // so anchor it at the business open hour (default 9am) on that day, in the
  // business tz. An assigned job reschedules via /move (keeps its tech + checks
  // conflicts); an unassigned one uses /schedule (time-only) — the NOT-317
  // drag-to-assign path passes technicianId=null and keeps using /schedule.
  const rescheduleOnDay = useCallback(
    (bookingId: string, technicianId: string | null, dayKey: string) => {
      const openHm = (monthSettings?.businessHoursStart || '09:00').slice(0, 5);
      const startAt = zonedDateTimeToUtc(
        parseLocalDate(dayKey),
        openHm,
        monthTenantTz,
      )?.toISOString();
      if (!startAt) return;
      const techName = technicians.find((t) => t.id === technicianId)?.name;
      void runWithOverride(
        (overrideConflicts, arrivalWindowChange, notification) =>
          technicianId
            ? moveBooking.mutateAsync({
                id: bookingId,
                dto: {
                  technicianId,
                  startAt,
                  overrideConflicts,
                  manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
                  arrivalWindowChange,
                  notification,
                },
              })
            : scheduleBooking.mutateAsync({
                id: bookingId,
                startAt,
                overrideConflicts,
                manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
                arrivalWindowChange,
                notification,
              }),
        {
          appointmentId: bookingId,
          title: 'This move has a conflict',
          confirmLabel: 'Move anyway',
          technicians: technicianId ? [{ id: technicianId, name: techName ?? 'Technician' }] : [],
        },
      )
        .then((saved) => {
          if (saved && technicianId) toast.success('Booking rescheduled');
        })
        .catch((err: Error) => toast.error(err.message || 'Could not reschedule'));
    },
    [
      scheduleBooking,
      moveBooking,
      runWithOverride,
      technicians,
      monthSettings?.businessHoursStart,
      monthTenantTz,
    ],
  );

  const today = todayInTz(monthBusinessTz);
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  // NOT-320 — capacity denominator scopes to the assignee filter's technicians
  // (crews resolved to their members), else all; mirrors the week grid.
  const scopedTechnicians = useMemo(() => {
    const picked = technicians.filter((t) => capacityTechnicianIds.includes(t.id));
    return picked.length ? picked : technicians;
  }, [technicians, capacityTechnicianIds]);
  const scopedBookings = useMemo(
    () => bookings.filter((b) => matchesFilters(b, filters)),
    [bookings, filters],
  );
  // NOT-317/320/328 — the Job tray (No date + No technician), filtered by every
  // dimension except assignee (tray items have no relevant technician).
  // §15 — day-bound (unsequenced) rows are intentionally NOT in the tray: the
  // amber Unsequenced chips carry them, and tray cards drag through the generic
  // /schedule path that route-owned visits must never take.
  const trayFilters = useMemo(() => ({ ...filters, assignees: [] }), [filters]);
  const filteredUnscheduled = useMemo(
    () => unscheduled.filter((b) => isUnassignedBooking(b) && matchesFilters(b, trayFilters)),
    [unscheduled, trayFilters],
  );
  const noTechBookings = useMemo(
    () =>
      bookings.filter(
        (b) => isUnassignedBooking(b) && b.status !== 'CANCELLED' && matchesFilters(b, trayFilters),
      ),
    [bookings, trayFilters],
  );

  // NOT-330 — full-day time-off / non-working techs per day, scoped to the techs
  // in view (mirrors the Week grid). One tech in view → name the reason + hatch
  // the cell; several → a compact "N off" pill (names in the title).
  const offForDay = useCallback(
    (key: string) =>
      scopedTechnicians
        .map((t) => ({
          tech: t,
          off: techDayUnavailability(t, parseLocalDate(key), monthTenantTz),
        }))
        .filter((x): x is { tech: SchedulerTechnician; off: { label: string; tooltip: string } } =>
          Boolean(x.off),
        ),
    [scopedTechnicians, monthTenantTz],
  );

  // Build the calendar grid (6 rows x 7 cols), rows starting on the tenant's
  // configured week-start (NOT-322).
  const calendarCells = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    // Offset of the 1st of the month from the configured week-start (getDay: 0=Sun).
    const startOffset = (getDay(firstDay) - weekStartsOn + 7) % 7;
    const gridStart = subDays(firstDay, startOffset);
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      cells.push(addDays(gridStart, i));
    }
    return { cells, lastDay };
  }, [year, month, weekStartsOn]);

  const bookingsByDay = useMemo(() => {
    const map = new Map<string, ScheduledBooking[]>();
    for (const b of scopedBookings) {
      if (b.status === 'CANCELLED') continue;
      // The day cell and the clock rendered inside it must come from ONE zone
      // (WeekView already buckets on `businessTz` this way). Keying on the
      // company stamp while the card renders in the display zone puts a visit
      // near midnight in one day cell showing a time that reads as the day
      // either side of it.
      //
      // Resolved PER BOOKING, not page-wide: `displayTimezone` is only the
      // fallback for a caller with no record in hand, so in `service` mode a
      // page-wide zone would bucket a Vancouver visit on the company clock. Two
      // visits on this grid can legitimately resolve to different zones — each
      // then lands on the day it happens LOCALLY, which is the whole point of
      // reading the schedule on the customer's clock.
      const key = getBookingDayKey(b.startAt, monthZoneFor(b));
      const list = map.get(key) ?? [];
      list.push(b);
      map.set(key, list);
    }
    return map;
  }, [scopedBookings, monthZoneFor]);

  // §15 — unsequenced DAY_FLEXIBLE rows keyed by their tenant-local
  // `scheduledDate` (no startAt to key on), scoped by the same filters.
  const unsequencedByDay = useMemo(() => {
    const map = new Map<string, SchedulerBooking[]>();
    for (const b of unsequenced) {
      if (!b.scheduledDate || !matchesFilters(b, filters)) continue;
      const list = map.get(b.scheduledDate) ?? [];
      list.push(b);
      map.set(b.scheduledDate, list);
    }
    return map;
  }, [unsequenced, filters]);

  const expandedBookings = useMemo(() => {
    if (!expandedDay) return [];
    return bookingsByDay.get(expandedDay) ?? [];
  }, [expandedDay, bookingsByDay]);

  const expandedTasks = useMemo(() => {
    if (!expandedDay) return [];
    return tasksByDay.get(expandedDay) ?? [];
  }, [expandedDay, tasksByDay]);

  // §15 — the expanded-day panel must show the day's unsequenced route
  // visits too (the grid cell renders their chips; hiding them here made
  // a route-only day expand to 'no visits').
  const expandedUnsequenced = useMemo(() => {
    if (!expandedDay) return [];
    return unsequencedByDay.get(expandedDay) ?? [];
  }, [expandedDay, unsequencedByDay]);

  const techMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of scopedTechnicians) m.set(t.id, t.name);
    return m;
  }, [scopedTechnicians]);

  // Max booking count for intensity scaling — the denominator must see the
  // same population the per-cell count does (incl. unsequenced day-bound
  // visits), or a route-only month renders chips with zero tint and mixed
  // months overstate intensity.
  const maxCount = useMemo(() => {
    let max = 0;
    const keys = new Set([
      ...bookingsByDay.keys(),
      ...tasksByDay.keys(),
      ...unsequencedByDay.keys(),
    ]);
    for (const key of keys) {
      const total =
        (bookingsByDay.get(key)?.length ?? 0) +
        (tasksByDay.get(key)?.length ?? 0) +
        (unsequencedByDay.get(key)?.length ?? 0);
      if (total > max) max = total;
    }
    return max;
  }, [bookingsByDay, tasksByDay, unsequencedByDay]);

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-auto bg-muted/20">
        {/* Calendar grid */}
        <div className="p-4">
          {/* Day name headers */}
          <div className="grid grid-cols-7 gap-px">
            {Array.from({ length: 7 }, (_, i) => WEEKDAY_SHORT[(weekStartsOn + i) % 7]).map((d) => (
              <div key={d} className="py-1.5 text-center text-xs font-medium text-muted-foreground">
                {d}
              </div>
            ))}
          </div>
          {/* Grid */}
          <div className="grid grid-cols-7 overflow-hidden rounded-xl border bg-border shadow-sm">
            {calendarCells.cells.map((cellDate) => {
              const key = toDateString(cellDate);
              const isCurrentMonth = cellDate.getMonth() === month;
              const isCurrentDay = key === today;
              const isExpanded = key === expandedDay;
              const dayBookings = bookingsByDay.get(key) ?? [];
              const dayTasks = tasksByDay.get(key) ?? [];
              // §15 — day-bound visits still awaiting the route optimizer.
              const dayUnsequenced = unsequencedByDay.get(key) ?? [];
              // NOT-324 — show every job in the cell; nothing hidden behind "+N more".
              const visibleTasks = dayTasks;
              const visibleBookings = dayBookings;
              // §15 — unsequenced day-bound visits are booked work: the count,
              // intensity tint and capacity pill must all see them.
              // Visits vs cell activity: the capacity chip compares VISITS
              // (timed + day-bound) against tech capacity; tasks only join the
              // intensity/count signal.
              const visitCount = dayBookings.length + dayUnsequenced.length;
              const count = visitCount + dayTasks.length;
              // NOT-330 — full-day time-off for the techs in view. One tech →
              // hatch the cell + name the reason; several → a small "N off" pill.
              const off = offForDay(key);
              const singleTech = scopedTechnicians.length === 1;
              // Capacity excludes techs fully off that day so the pill never
              // contradicts the time-off state (mirrors the Week header).
              const offIds = new Set(off.map((o) => o.tech.id));
              const dayCap = totalDayCapacity(scopedTechnicians.filter((t) => !offIds.has(t.id)));
              // Compute background intensity
              const intensity = maxCount > 0 ? count / maxCount : 0;
              const bgClass = isCurrentDay
                ? 'bg-primary/10'
                : intensity > 0.7
                  ? 'bg-blue-100 dark:bg-blue-900/40'
                  : intensity > 0.3
                    ? 'bg-blue-50 dark:bg-blue-950/30'
                    : 'bg-background';

              return (
                <div
                  key={key}
                  role="button"
                  tabIndex={0}
                  onClick={() => onExpandDay(isExpanded ? null : key)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    onExpandDay(isExpanded ? null : key);
                  }}
                  // NOT-317 — drop an unscheduled card to schedule it on this day;
                  // NOT-325 — drop an existing job chip to reschedule it here.
                  onDragOver={(event) => {
                    const { types } = event.dataTransfer;
                    if (
                      !types.includes(UNSCHEDULED_DND_TYPE) &&
                      !types.includes(BOOKING_MOVE_DND_TYPE)
                    )
                      return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    if (dragOverDay !== key) setDragOverDay(key);
                  }}
                  onDragLeave={() => setDragOverDay((d) => (d === key ? null : d))}
                  onDrop={(event) => {
                    // NOT-325 — an existing job chip carries its id + current tech
                    // + source day.
                    const moveRaw = event.dataTransfer.getData(BOOKING_MOVE_DND_TYPE);
                    if (moveRaw) {
                      event.preventDefault();
                      setDragOverDay(null);
                      const { bookingId, technicianId, fromDayKey } = JSON.parse(moveRaw) as {
                        bookingId: string;
                        technicianId: string | null;
                        fromDayKey: string;
                      };
                      // Dropped back on the same day → no-op. Otherwise a 2:30pm
                      // job would be silently reset to the business open hour.
                      if (fromDayKey === key) return;
                      rescheduleOnDay(bookingId, technicianId, key);
                      return;
                    }
                    // NOT-317 — an unscheduled card (no tech yet).
                    const bookingId = event.dataTransfer.getData(UNSCHEDULED_DND_TYPE);
                    if (!bookingId) return;
                    event.preventDefault();
                    setDragOverDay(null);
                    rescheduleOnDay(bookingId, null, key);
                  }}
                  className={cn(
                    'group relative min-h-[104px] p-2 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                    bgClass,
                    // One tech in view + that tech is off all day → hatch the cell.
                    singleTech && off.length > 0 && 'cal-hatch',
                    isExpanded && 'ring-2 ring-primary ring-inset',
                    dragOverDay === key && 'ring-2 ring-primary ring-inset',
                    !isCurrentMonth && 'opacity-40',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={cn(
                        'text-sm font-medium',
                        isCurrentDay &&
                          'flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground',
                      )}
                    >
                      {cellDate.getDate()}
                    </span>
                    {visitCount > 0 && dayCap > 0 ? (
                      <span
                        title="Booked / capacity"
                        className={cn(
                          'mr-5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                          capacityChipClasses(visitCount, dayCap),
                        )}
                      >
                        {visitCount}/{dayCap}
                      </span>
                    ) : count > 0 ? (
                      <Badge variant="secondary" className="mr-5 text-xs">
                        {count}
                      </Badge>
                    ) : null}
                  </div>
                  {off.length > 0 ? (
                    <span
                      className="mt-1 flex w-fit items-center gap-1 rounded bg-muted/70 px-1.5 py-0.5 text-[10px] text-muted-foreground"
                      title={
                        singleTech
                          ? off[0].off.tooltip
                          : off.map((o) => `${o.tech.name}: ${o.off.label}`).join('\n')
                      }
                    >
                      <Ban className="size-2.5" />
                      {singleTech ? off[0].off.label : `${off.length} off`}
                    </span>
                  ) : null}
                  {count > 0 || dayUnsequenced.length > 0 ? (
                    <div className="mt-1.5 flex min-w-0 flex-col gap-1">
                      {visibleTasks.map((task) => (
                        <CalendarTaskChip
                          key={task.id}
                          task={task}
                          onClick={() => onTaskClick(task)}
                          compact
                        />
                      ))}
                      {visibleBookings.map((booking) => (
                        <button
                          key={booking.bookingId}
                          type="button"
                          // NOT-325 — drag a job chip onto another day cell to
                          // reschedule it (kept tech via /move, else /schedule).
                          // Day-bound (ALL_DAY) visits move by DAY via the visit
                          // day actions, never this generic drag.
                          draggable={
                            booking.granularity !== 'ALL_DAY' &&
                            booking.granularity !== 'DAY_FLEXIBLE'
                          }
                          onDragStart={(event) => {
                            event.stopPropagation();
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData(
                              BOOKING_MOVE_DND_TYPE,
                              JSON.stringify({
                                bookingId: booking.bookingId,
                                technicianId: booking.technicianId ?? null,
                                // Source day — the drop handler no-ops a same-day
                                // drop so it doesn't reset the time to open hour.
                                fromDayKey: key,
                              }),
                            );
                            // Firefox/Safari can refuse to start a native drag
                            // without a standard MIME type — mirror the unscheduled
                            // card path so chip drag works outside Chromium too.
                            event.dataTransfer.setData('text/plain', booking.bookingId);
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            onBookingClick(booking);
                          }}
                          className={cn(
                            'relative flex min-w-0 items-center gap-1.5 rounded border border-transparent px-1.5 py-0.5 text-left text-[11px] leading-4 transition-[filter] hover:brightness-95',
                            // §15 — ring siblings of the series open in the sheet.
                            isSeriesSibling(booking, seriesKey) && 'ring-2 ring-primary/60',
                            // Lifted clear of the sheet's scrim, not tinted.
                            booking.bookingId === selectedBookingId && 'dispatch-selected',
                          )}
                          style={bookingJobTypeStyle(booking, resolveJobTypeFamily)}
                        >
                          {/* NOT-329 — one line: status dot + customer + time. */}
                          <span
                            className={cn(
                              'size-1.5 shrink-0 rounded-full',
                              getStatusDotClass(booking.status),
                            )}
                          />
                          <JobTypeIcon icon={booking.jobType?.icon} />
                          {booking.isRecurring ? <RecurringBadge className="size-2.5" /> : null}
                          <CompletedTileCheck status={booking.status} />
                          <span className="truncate font-medium text-foreground">
                            {tileTitle(booking)}
                          </span>
                          <JobTagChips tags={booking.tags} max={2} compact />
                          {booking.startAt && booking.granularity !== 'ALL_DAY' ? (
                            <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                              {formatDateTimeInTimeZone(booking.startAt, monthZoneFor(booking), {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                          ) : null}
                        </button>
                      ))}
                      {/* §15 — unsequenced DAY_FLEXIBLE visits: day-placed via
                          scheduledDate, no time yet (the route optimizer assigns
                          it). Amber mini-chip (shared molecule), left click opens
                          the detail sheet, right click a small context menu; not
                          draggable — route moves go via the route actions. */}
                      {dayUnsequenced.map((booking) => (
                        <UnsequencedChip
                          key={booking.bookingId}
                          booking={booking}
                          onOpenDetails={onBookingClick}
                          highlighted={isSeriesSibling(booking, seriesKey)}
                        />
                      ))}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Add booking on ${format(cellDate, 'MMM d')}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onAddBooking({ date: key });
                    }}
                    className="absolute top-1 right-1 rounded p-1 text-muted-foreground opacity-0 transition-colors transition-opacity hover:bg-muted/60 hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Expanded day bookings */}
        {expandedDay && (
          <div className="border-t bg-background px-4 pb-4">
            <div className="flex items-center gap-2 py-3">
              <h3 className="text-sm font-semibold">
                {format(parseLocalDate(expandedDay), 'EEEE, MMMM d')}
              </h3>
              <Badge variant="secondary" className="text-xs">
                {expandedBookings.length + expandedUnsequenced.length} visit
                {expandedBookings.length + expandedUnsequenced.length !== 1 ? 's' : ''}
              </Badge>
              {expandedTasks.length > 0 ? (
                <Badge variant="outline" className="text-xs">
                  {expandedTasks.length} task{expandedTasks.length !== 1 ? 's' : ''}
                </Badge>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 text-xs"
                onClick={() => onExpandDay(null)}
              >
                Close
              </Button>
            </div>
            {expandedBookings.length === 0 &&
            expandedTasks.length === 0 &&
            expandedUnsequenced.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No visits or tasks on this day
              </p>
            ) : (
              <div className="space-y-3">
                {expandedTasks.length > 0 ? (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {expandedTasks.map((task) => (
                      <CalendarTaskCard
                        key={task.id}
                        task={task}
                        onClick={() => onTaskClick(task)}
                      />
                    ))}
                  </div>
                ) : null}
                {expandedBookings.length > 0 ? (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {expandedBookings.map((b) => (
                      <button
                        key={b.bookingId}
                        onClick={() => onBookingClick(b)}
                        style={bookingJobTypeStyle(b, resolveJobTypeFamily)}
                        className={cn(
                          'rounded-lg border p-3 text-left transition-colors hover:opacity-80',
                          !resolveJobTypeHex(b, resolveJobTypeFamily) &&
                            getStatusBlockClasses(b.status),
                          // §15 — ring siblings of the series open in the sheet.
                          isSeriesSibling(b, seriesKey) && 'ring-2 ring-primary/60',
                          b.bookingId === selectedBookingId && 'dispatch-selected',
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <p className="flex items-center gap-1 truncate text-sm font-medium">
                            {b.isRecurring ? <RecurringBadge /> : null}
                            <CompletedTileCheck status={b.status} />
                            <span className="truncate">{b.customerName}</span>
                          </p>
                          <Badge variant={STATUS_BADGE_VARIANT[b.status]} className="ml-2 text-xs">
                            {b.status}
                          </Badge>
                        </div>
                        <JobTagChips tags={b.tags} max={4} className="mt-1 flex-wrap" />
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatTimeRange(b.startAt, b.endAt, monthZoneFor(b))}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          <span className="flex min-w-0 items-center gap-1">
                            <JobTypeIcon icon={b.jobType?.icon} />
                            <span className="truncate">{b.serviceType.name}</span>
                          </span>
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {b.technicianId
                            ? (techMap.get(b.technicianId) ?? b.technicianName ?? 'Assigned')
                            : 'Unassigned'}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : null}
                {expandedUnsequenced.length > 0 ? (
                  <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {expandedUnsequenced.map((b) => (
                      <UnsequencedChip
                        key={b.bookingId}
                        booking={b}
                        onOpenDetails={onBookingClick}
                        highlighted={isSeriesSibling(b, seriesKey)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
      <CalendarSideRail
        unscheduled={filteredUnscheduled}
        noTech={noTechBookings}
        cancelled={cancelled}
        businessTz={monthBusinessTz}
        dragMode="html5"
        assigneeFiltered={filters.assignees.length > 0}
      />
      {overrideDialog}
    </div>
  );
}

// NOT-326 — week split by technician: tech rows grouped into collapsible crew
// sections, days as columns, each cell the tech's jobs that day. Custom grid (no
// FullCalendar), mirroring MonthView. Drag a job to another row to reassign, to
// another column to reschedule.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

function TechnicianView({
  dateObj,
  bookings,
  unsequenced,
  technicians,
  crews,
  filters,
  weekStartsOn,
  selectedBookingId,
  seriesKey,
  onBookingClick,
  onAddBooking,
  tasksByDay,
  onTaskClick,
}: {
  /** Open tasks by day key; each lands in its technician's row. */
  tasksByDay: CalendarTasksByDay;
  onTaskClick: (task: Task) => void;
  dateObj: Date;
  bookings: ScheduledBooking[];
  /** §15 — day-bound DAY_FLEXIBLE visits with no time yet; placed by scheduledDate. */
  unsequenced: SchedulerBooking[];
  technicians: SchedulerTechnician[];
  crews: SchedulerCrew[];
  filters: CalendarFilters;
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  selectedBookingId?: string;
  /** §15 — the open sheet's series key (parent jobId); rings sibling tiles. */
  seriesKey?: string;
  onBookingClick: (b: SchedulerBooking) => void;
  onAddBooking: (prefill: CreateBookingPrefill) => void;
}) {
  // Same Trade-palette resolver the map pins use, so a tile and its pin can
  // never disagree about a booking with no persisted job-type colour.
  const resolveJobTypeFamily = useJobTypeFamilyResolver();
  const { data: settings } = useBusinessSettings();
  const tenantTz = settings?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const businessTz = useScheduleTimezone().displayTimezone;
  const moveBooking = useMoveBooking();
  const { run: runWithOverride, dialog: overrideDialog } = useConflictOverride({
    allowArrivalWindowChange: true,
  });
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);

  // The week's 7 days, from the configured week-start.
  const days = useMemo(() => {
    const start = startOfWeek(dateObj, { weekStartsOn });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [dateObj, weekStartsOn]);

  // A task's row: its technician, or — for a task assigned to a login — the
  // technician behind that login. Tasks for office staff have no row here.
  const tasksByTechDay = useMemo(() => {
    const techByUser = new Map(
      technicians.filter((tech) => tech.authUserId).map((tech) => [tech.authUserId!, tech.id]),
    );
    const map = new Map<string, Map<string, Task[]>>();
    for (const [dayKey, tasks] of tasksByDay) {
      for (const task of tasks) {
        const techId =
          task.technicianId ?? (task.assignedTo ? techByUser.get(task.assignedTo) : undefined);
        if (!techId) continue;
        const byDay = map.get(techId) ?? new Map<string, Task[]>();
        byDay.set(dayKey, [...(byDay.get(dayKey) ?? []), task]);
        map.set(techId, byDay);
      }
    }
    return map;
  }, [tasksByDay, technicians]);

  // Active (non-cancelled, filter-matching) bookings grouped by tech → dayKey.
  const byTechDay = useMemo(() => {
    const map = new Map<string, Map<string, ScheduledBooking[]>>();
    for (const b of bookings) {
      if (b.status === 'CANCELLED' || !matchesFilters(b, filters)) continue;
      const techKey = b.technicianId ?? UNASSIGNED_KEY;
      const startKey = getBookingDayKey(b.startAt, businessTz);
      if (!startKey) continue;
      let perDay = map.get(techKey);
      if (!perDay) {
        perDay = new Map();
        map.set(techKey, perDay);
      }
      // Expand multi-day / all-day spans across every day they cover, so a
      // Mon–Wed visit shows on Tue/Wed too (a free-looking cell would invite
      // double-booking). Single-day → one entry. Mirrors the week grid.
      const endKey = b.endAt ? getBookingDayKey(b.endAt, businessTz) : startKey;
      let key: string | null = startKey;
      while (key && key <= (endKey ?? startKey)) {
        const list = perDay.get(key) ?? [];
        list.push(b);
        perDay.set(key, list);
        if (key === endKey) break;
        key = nextDayKey(key);
      }
    }
    for (const perDay of map.values()) {
      for (const list of perDay.values()) {
        list.sort((a, b) => a.startAt.localeCompare(b.startAt));
      }
    }
    return map;
  }, [bookings, filters, businessTz]);

  // §15 — unsequenced DAY_FLEXIBLE visits indexed tech → scheduledDate (their
  // tenant-local day; no startAt to key on), scoped by the same filters. Tech-less
  // rows land in the Unassigned row, mirroring the timed grouping above.
  const unsequencedByTechDay = useMemo(() => {
    const map = new Map<string, Map<string, SchedulerBooking[]>>();
    for (const b of unsequenced) {
      if (!b.scheduledDate || !matchesFilters(b, filters)) continue;
      const techKey = b.technicianId ?? UNASSIGNED_KEY;
      let perDay = map.get(techKey);
      if (!perDay) {
        perDay = new Map();
        map.set(techKey, perDay);
      }
      const list = perDay.get(b.scheduledDate) ?? [];
      list.push(b);
      perDay.set(b.scheduledDate, list);
    }
    return map;
  }, [unsequenced, filters]);

  const techJobCount = useCallback(
    (techKey: string) => {
      // Dedupe by bookingId — multi-day spans are indexed under each day they
      // cover, so a plain sum would over-count them.
      const ids = new Set<string>();
      for (const list of byTechDay.get(techKey)?.values() ?? [])
        for (const b of list) ids.add(b.bookingId);
      // §15 — day-bound unsequenced visits are this tech's work too; without
      // them a row holding only amber chips would read "0 jobs".
      for (const list of unsequencedByTechDay.get(techKey)?.values() ?? [])
        for (const b of list) ids.add(b.bookingId);
      return ids.size;
    },
    [byTechDay, unsequencedByTechDay],
  );

  // Crew sections: each crew with its members; techs in no crew go to a separate
  // "No crew" section. The "Unassigned jobs" row (dated, tech-less bookings) is
  // rendered as its own top section, not here.
  const sections = useMemo(() => {
    const techById = new Map(technicians.map((t) => [t.id, t]));
    const claimed = new Set<string>();
    const crewSections = (crews ?? [])
      .filter((c) => c.isActive !== false)
      .map((crew) => {
        const members = (crew.technicians ?? [])
          .map((m) => techById.get(m.id))
          .filter((t): t is SchedulerTechnician => Boolean(t));
        members.forEach((t) => claimed.add(t.id));
        return { id: `crew:${crew.id}`, name: crew.name, technicians: members };
      })
      .filter((s) => s.technicians.length > 0);
    const looseTechs = technicians.filter((t) => !claimed.has(t.id));
    return { crewSections, looseTechs };
  }, [crews, technicians]);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // NOT-330 — per-cell unavailability (see module-level techDayUnavailability).
  const unavailabilityFor = useCallback(
    (tech: SchedulerTechnician, day: Date) => techDayUnavailability(tech, day, tenantTz),
    [tenantTz],
  );

  const techNameOf = useCallback(
    (id: string) => technicians.find((t) => t.id === id)?.name ?? 'Technician',
    [technicians],
  );

  const dropOnCell = useCallback(
    (techKey: string, day: Date) => (event: DragEvent) => {
      const targetDayKey = toDateString(day);
      const openHm = (settings?.businessHoursStart || '09:00').slice(0, 5);

      // A card dragged from the Job tray (no date, or no technician) → assign it
      // to this technician, anchored at the day's open hour.
      const trayId = event.dataTransfer.getData(UNSCHEDULED_DND_TYPE);
      if (trayId) {
        event.preventDefault();
        setDragOverCell(null);
        const startAt = zonedDateTimeToUtc(day, openHm, tenantTz)?.toISOString();
        if (!startAt) return;
        void runWithOverride(
          (overrideConflicts, arrivalWindowChange, notification) =>
            moveBooking.mutateAsync({
              id: trayId,
              dto: {
                technicianId: techKey,
                startAt,
                overrideConflicts,
                manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
                arrivalWindowChange,
                notification,
              },
            }),
          {
            appointmentId: trayId,
            title: 'This move has a conflict',
            confirmLabel: 'Move anyway',
            technicians: [{ id: techKey, name: techNameOf(techKey) }],
          },
        )
          .then((saved) => {
            if (saved) toast.success('Booking assigned');
          })
          .catch((err: Error) => toast.error(err.message || 'Could not assign'));
        return;
      }

      // TECH_MOVE = a chip dragged within the grid; BOOKING_MOVE = a scheduled
      // "No technician" card from the Job tray. Same payload shape → same logic
      // (assign to this tech, preserving the time on a same-day drop).
      const raw =
        event.dataTransfer.getData(TECH_MOVE_DND_TYPE) ||
        event.dataTransfer.getData(BOOKING_MOVE_DND_TYPE);
      if (!raw) return;
      event.preventDefault();
      setDragOverCell(null);
      const payload = JSON.parse(raw) as {
        bookingId: string;
        technicianId: string | null;
        startAt: string;
        allDay: boolean;
        fromDayKey: string;
      };
      const { bookingId } = payload;
      // Use the SOURCE CELL's day (not the booking's startAt day) — a multi-day
      // span is cloned into each covered column, so a Tue chip of a Mon–Wed job
      // must read as "Tuesday", else a same-day row move looks like a day change.
      const sameDay = payload.fromDayKey === targetDayKey;

      // Same tech + same day = no change → skip (a row/column move that lands
      // where it started shouldn't fire a mutation or rewrite the time).
      if (sameDay && (payload.technicianId ?? UNASSIGNED_KEY) === techKey) return;

      // Preserve the original time-of-day: keep startAt unchanged on a same-day
      // move; on a day change copy the original business-time HH:mm onto the
      // target date (all-day spans have no time → anchor to the open hour).
      let startAt: string | undefined;
      if (sameDay) {
        startAt = payload.startAt;
      } else {
        if (payload.allDay) {
          startAt = zonedDateTimeToUtc(day, openHm, tenantTz)?.toISOString();
        } else {
          const hhmm = formatDateTimeInTimeZone(payload.startAt, businessTz, {
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          });
          startAt = zonedDateTimeToUtc(day, hhmm, businessTz)?.toISOString();
        }
      }
      if (!startAt) return;

      // Only real technician cells are drop targets (the Unassigned row is
      // source-only) → assign/reassign + (re)schedule; a conflict asks for an
      // acknowledgement before the move goes through.
      void runWithOverride(
        (overrideConflicts, arrivalWindowChange, notification) =>
          moveBooking.mutateAsync({
            id: bookingId,
            dto: {
              technicianId: techKey,
              startAt,
              overrideConflicts,
              manualScheduling: schedulingPrefix() === '/v2' ? true : undefined,
              arrivalWindowChange,
              notification,
            },
          }),
        {
          appointmentId: bookingId,
          title: 'This move has a conflict',
          confirmLabel: 'Move anyway',
          technicians: [{ id: techKey, name: techNameOf(techKey) }],
        },
      )
        .then((saved) => {
          if (saved) toast.success('Booking rescheduled');
        })
        .catch((err: Error) => toast.error(err.message || 'Could not reschedule'));
    },
    [moveBooking, runWithOverride, techNameOf, settings?.businessHoursStart, businessTz, tenantTz],
  );

  const renderCell = (techKey: string, tech: SchedulerTechnician | null, day: Date) => {
    const dayKey = toDateString(day);
    const cellId = `${techKey}:${dayKey}`;
    const jobs = byTechDay.get(techKey)?.get(dayKey) ?? [];
    // Tasks sit beside the jobs but never count against the day: a task does
    // not reserve the technician (booking ignores them), it just shows up.
    const cellTasks = tasksByTechDay.get(techKey)?.get(dayKey) ?? [];
    // §15 — this tech's day-bound visits still awaiting the route optimizer.
    const cellUnsequenced = unsequencedByTechDay.get(techKey)?.get(dayKey) ?? [];
    const unavailable = tech ? unavailabilityFor(tech, day) : null;
    // The Unassigned-jobs row is a drag SOURCE only — it's not a drop target.
    // Routing a drop here through /schedule would keep an assigned job's tech
    // while the row communicates "no technician"; unassigning is the Dispatch
    // board's job. So only real technician cells (and available days) accept drops.
    const droppable = !unavailable && techKey !== UNASSIGNED_KEY;
    return (
      <div
        key={cellId}
        title={unavailable?.tooltip}
        onDragOver={(event) => {
          const { types } = event.dataTransfer;
          if (
            !droppable ||
            !(
              types.includes(TECH_MOVE_DND_TYPE) ||
              types.includes(BOOKING_MOVE_DND_TYPE) ||
              types.includes(UNSCHEDULED_DND_TYPE)
            )
          )
            return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          if (dragOverCell !== cellId) setDragOverCell(cellId);
        }}
        onDragLeave={() => setDragOverCell((c) => (c === cellId ? null : c))}
        onDrop={droppable ? dropOnCell(techKey, day) : undefined}
        className={cn(
          'flex min-h-[72px] flex-col gap-1 border-l border-t p-1.5',
          // NOT-330 — hatched (not flat) so unavailable reads distinct from a job
          // tile; closes the NOT-329 hatch gap.
          unavailable && 'cal-hatch bg-muted/30',
          dragOverCell === cellId && 'ring-2 ring-primary ring-inset',
        )}
      >
        {jobs.length > 0 || cellUnsequenced.length > 0 || cellTasks.length > 0 ? (
          <>
            {/* A normally-off day can still hold an exception booking — show the
                jobs (muted, non-droppable) with a small unavailable hint rather
                than hiding scheduled work behind the placeholder. */}
            {unavailable ? (
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <Ban className="size-2.5" />
                {unavailable.label}
              </span>
            ) : null}
            {jobs.map((b) => (
              <button
                key={b.bookingId}
                type="button"
                // Day-bound (ALL_DAY) visits move by DAY via the visit day
                // actions — dropping one on another cell would fire the generic
                // /move with a stale scheduledDate, so don't let it drag.
                draggable={b.granularity !== 'ALL_DAY' && b.granularity !== 'DAY_FLEXIBLE'}
                onDragStart={(event) => {
                  event.stopPropagation();
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData(
                    TECH_MOVE_DND_TYPE,
                    JSON.stringify({
                      bookingId: b.bookingId,
                      technicianId: b.technicianId ?? null,
                      startAt: b.startAt,
                      allDay: b.granularity === 'ALL_DAY',
                      // Source cell day — a multi-day span is cloned into each
                      // column, so the same-day check must use this, not startAt.
                      fromDayKey: dayKey,
                    }),
                  );
                  // Firefox/Safari can refuse to start a native drag without a
                  // standard MIME type — mirror the month-chip path.
                  event.dataTransfer.setData('text/plain', b.bookingId);
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  onBookingClick(b);
                }}
                style={bookingJobTypeStyle(b, resolveJobTypeFamily)}
                className={cn(
                  // NOT-329 — pastel status tint + left bar (same look as the board);
                  // single-side border → no rounded corners.
                  // #1976 — the badges are in-flow siblings now, so the tile is a
                  // row with no `relative`; the body span below owns the column.
                  'flex min-w-0 gap-1 border px-2 py-1 text-left text-[11px] leading-tight transition-colors hover:brightness-95',
                  ...(resolveJobTypeHex(b, resolveJobTypeFamily)
                    ? []
                    : getStatusBlockTint(b.status).split(' ')),
                  getStatusBarClass(b.status),
                  b.status === 'CANCELLED' && 'opacity-70',
                  isPendingWeatherHold(b) && 'cal-hatch',
                  b.bookingId === selectedBookingId && 'ring-2 ring-primary ring-inset',
                  // §15 — ring the OTHER visits of the series open in the sheet
                  // (the selected tile keeps its stronger solid ring above).
                  isSeriesSibling(b, seriesKey) &&
                    b.bookingId !== selectedBookingId &&
                    'ring-2 ring-primary/60 ring-inset',
                )}
              >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    className={cn(
                      'truncate font-medium text-foreground',
                      b.status === 'CANCELLED' && 'line-through',
                    )}
                  >
                    {tileTitle(b)}
                  </span>
                  <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
                    <JobTypeIcon icon={b.jobType?.icon} />
                    <span className="truncate">
                      {b.serviceType.name}
                      {visitLabel(b) ? ` · ${visitLabel(b)}` : ''}
                    </span>
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {b.granularity === 'ALL_DAY'
                      ? 'Full Day'
                      : `${formatDateTimeInTimeZone(b.startAt, businessTz, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}`}
                  </span>
                </span>
                {/* NOT-329 — status badges top-right (every mode), in flow. */}
                <TileBadges booking={b} className="shrink-0 self-start" />
              </button>
            ))}
            {/* §15 — day-bound visits awaiting the route optimizer: same amber
                chip as the Month grid (shared molecule). Not draggable; left
                click opens the sheet, right click the context menu. */}
            {cellUnsequenced.map((b) => (
              <UnsequencedChip
                key={b.bookingId}
                booking={b}
                onOpenDetails={onBookingClick}
                highlighted={isSeriesSibling(b, seriesKey)}
              />
            ))}
            {cellTasks.map((task) => (
              <CalendarTaskChip
                key={task.id}
                task={task}
                compact
                onClick={() => onTaskClick(task)}
              />
            ))}
          </>
        ) : unavailable ? (
          <span className="m-auto flex items-center gap-1 text-[11px] text-muted-foreground">
            <Ban className="size-3" />
            {unavailable.label}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onAddBooking({ date: dayKey, technicianId: tech?.id })}
            className="m-auto flex items-center gap-1 text-[11px] text-muted-foreground/60 transition-colors hover:text-foreground"
          >
            <Plus className="size-3" />
            Available
          </button>
        )}
      </div>
    );
  };

  // A tech row is emitted as flat grid children (tech cell + 7 day cells) so they
  // align to the 8 columns — no nested grid/subgrid.
  const renderTechRow = (techKey: string, tech: SchedulerTechnician | null, label: string) => (
    <Fragment key={techKey}>
      <div className="sticky left-0 z-10 flex items-center gap-2 border-l border-t bg-card px-2 py-1.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-foreground">
          {tech ? initials(tech.name) : <User className="size-3.5" />}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate text-xs font-medium text-foreground">{label}</p>
          <span className="w-fit rounded bg-muted px-1.5 text-[10px] text-muted-foreground">
            {techJobCount(techKey)} jobs
          </span>
        </div>
      </div>
      {days.map((day) => renderCell(techKey, tech, day))}
    </Fragment>
  );

  const sectionHeader = (id: string, label: string, count?: string) => (
    <button
      key={id}
      type="button"
      onClick={() => toggle(id)}
      className="col-span-full flex items-center gap-1.5 border-l border-t bg-muted/30 px-2 py-1.5 text-left text-xs font-medium hover:bg-muted/50"
    >
      {collapsed.has(id) ? (
        <ChevronRight className="size-3.5 text-muted-foreground" />
      ) : (
        <ChevronDown className="size-3.5 text-muted-foreground" />
      )}
      <span className="text-foreground">{label}</span>
      {count ? (
        <span className="rounded-full border px-2 text-[10px] font-normal text-muted-foreground">
          {count}
        </span>
      ) : null}
    </button>
  );

  const gridStyle = { gridTemplateColumns: '150px repeat(7, minmax(0, 1fr))' } as const;

  return (
    <div className="min-h-0 flex-1 overflow-auto bg-muted/20 p-3">
      <div className="grid w-full border-b border-r bg-border text-sm" style={gridStyle}>
        {/* Header row */}
        <div className="sticky left-0 z-10 border-l border-t bg-card p-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Technician
        </div>
        {days.map((day) => (
          <div key={toDateString(day)} className="border-l border-t bg-card p-2 text-center">
            <div className="text-[11px] text-muted-foreground">{WEEKDAY_SHORT[getDay(day)]}</div>
            <div className="text-sm font-semibold">{format(day, 'd')}</div>
          </div>
        ))}

        {/* Unassigned jobs — dated bookings with no technician (the assign backlog).
            Pinned at the top and always shown: it's the drag-to-assign source. */}
        <Fragment>
          {sectionHeader(
            'unassigned-jobs',
            'Unassigned jobs',
            `${techJobCount(UNASSIGNED_KEY)} jobs`,
          )}
          {!collapsed.has('unassigned-jobs') &&
            renderTechRow(UNASSIGNED_KEY, null, 'Unassigned jobs')}
        </Fragment>

        {/* Crew sections */}
        {sections.crewSections.map((section) => (
          <Fragment key={section.id}>
            {sectionHeader(section.id, section.name, `${section.technicians.length} techs`)}
            {!collapsed.has(section.id) &&
              section.technicians.map((tech) => renderTechRow(tech.id, tech, tech.name))}
          </Fragment>
        ))}

        {/* Technicians in no crew. */}
        {sections.looseTechs.length > 0 && (
          <Fragment>
            {sectionHeader('crew:none', 'No crew', `${sections.looseTechs.length} techs`)}
            {!collapsed.has('crew:none') &&
              sections.looseTechs.map((tech) => renderTechRow(tech.id, tech, tech.name))}
          </Fragment>
        )}
      </div>
      {overrideDialog}
    </div>
  );
}

function MobileAgendaView({
  view,
  dateObj,
  bookings,
  unsequenced,
  tasksByDay,
  filters,
  onBookingClick,
  onTaskClick,
}: {
  view: ViewType;
  dateObj: Date;
  bookings: ScheduledBooking[];
  /** §15 — day-bound DAY_FLEXIBLE rows (scheduledDate, no time yet). */
  unsequenced: SchedulerBooking[];
  tasksByDay: CalendarTasksByDay;
  filters: CalendarFilters;
  onBookingClick: (b: SchedulerBooking) => void;
  onTaskClick: (task: Task) => void;
}) {
  const { data: mobileSettings } = useBusinessSettings();
  const displayTz = useScheduleTimezone().displayTimezone;
  const weekStartsOn = asWeekStart(mobileSettings?.weekStartDay);
  const workWeekStartsOn = asWeekStart(mobileSettings?.workWeekStartDay);
  const filteredBookings = useMemo(() => {
    // NOT-320 — cancelled visits are normally kept out of the agenda, but the
    // mobile path has no cancelled rail, so when the user explicitly filters by
    // CANCELLED we surface them inline instead of returning an empty agenda.
    const showCancelled = filters.statuses.includes('CANCELLED');
    return bookings.filter(
      (booking) =>
        (showCancelled || booking.status !== 'CANCELLED') && matchesFilters(booking, filters),
    );
  }, [bookings, filters]);

  const bookingsByDay = useMemo(() => {
    const map = new Map<string, ScheduledBooking[]>();
    for (const booking of filteredBookings) {
      const key = getBookingDayKey(booking.startAt, displayTz);
      const list = map.get(key) ?? [];
      list.push(booking);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.startAt.localeCompare(b.startAt));
    }
    return map;
  }, [filteredBookings, displayTz]);

  // §15 — unsequenced DAY_FLEXIBLE rows keyed by their tenant-local
  // scheduledDate (mirrors the MonthView pattern; same filter scoping).
  const agendaUnsequencedByDay = useMemo(() => {
    const map = new Map<string, SchedulerBooking[]>();
    for (const b of unsequenced) {
      if (!b.scheduledDate || !matchesFilters(b, filters)) continue;
      const list = map.get(b.scheduledDate) ?? [];
      list.push(b);
      map.set(b.scheduledDate, list);
    }
    return map;
  }, [unsequenced, filters]);

  const days = useMemo(() => {
    // NOT-326 — the desktop Technician grid has no mobile renderer; fall back to
    // the same 7-day week agenda here (not the month-style logic, which can omit
    // empty days and bleed into the next month).
    if (view === 'week' || view === 'technician') {
      const weekStart = startOfWeek(dateObj, { weekStartsOn });
      return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
    }

    if (view === 'workweek') {
      const wwStart = startOfWeek(dateObj, { weekStartsOn: workWeekStartsOn });
      return Array.from({ length: 5 }, (_, index) => addDays(wwStart, index));
    }

    const bookingDates = Array.from(bookingsByDay.keys())
      .map(parseLocalDate)
      .filter((date) => date.getMonth() === dateObj.getMonth())
      .sort((a, b) => a.getTime() - b.getTime());
    const taskDates = Array.from(tasksByDay.keys())
      .map(parseLocalDate)
      .filter((date) => date.getMonth() === dateObj.getMonth());
    // §15 — a day holding only unsequenced day-bound visits is still a day
    // with work: without these keys a route-only month renders the empty
    // state and the visits are unreachable on mobile.
    const unsequencedDates = Array.from(agendaUnsequencedByDay.keys())
      .map(parseLocalDate)
      .filter((date) => date.getMonth() === dateObj.getMonth());
    const datesByKey = new Map<string, Date>();
    for (const date of [...bookingDates, ...taskDates, ...unsequencedDates]) {
      datesByKey.set(toDateString(date), date);
    }
    const dates = Array.from(datesByKey.values()).sort((a, b) => a.getTime() - b.getTime());

    return dates.length > 0 ? dates : [dateObj];
  }, [
    bookingsByDay,
    agendaUnsequencedByDay,
    dateObj,
    tasksByDay,
    view,
    weekStartsOn,
    workWeekStartsOn,
  ]);

  // A field tech's calendar is scoped to their own appointments (calendar:view
  // OWN), so "All technicians" would misrepresent what they're looking at.
  const calendarViewScope = usePermissionScope('calendar:view');

  // NOT-320 — summarize the active filters in the agenda header.
  const filterSummary = useMemo(() => {
    const count = activeFilterCount(filters);
    if (count > 0) {
      return `${count} filter${count > 1 ? 's' : ''} active`;
    }
    // No filters: reflect the scope of what's actually shown.
    if (calendarViewScope === 'OWN') return 'Just me';
    if (calendarViewScope === 'TEAM') return 'My team';
    return 'All technicians';
  }, [filters, calendarViewScope]);

  const totalUnsequencedCount = Array.from(agendaUnsequencedByDay.values()).reduce(
    (sum, list) => sum + list.length,
    0,
  );
  const totalTaskCount = Array.from(tasksByDay.values()).reduce(
    (sum, list) => sum + list.length,
    0,
  );
  const hasBookings = filteredBookings.length > 0 || totalUnsequencedCount > 0;
  const hasTasks = totalTaskCount > 0;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border bg-card p-3 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Agenda</p>
            <p className="truncate text-xs text-muted-foreground">
              {view === 'week' || view === 'workweek' || view === 'technician'
                ? filterSummary
                : format(dateObj, view === 'month' ? 'MMMM yyyy' : 'MMM d, yyyy')}
            </p>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {filteredBookings.length + totalTaskCount + totalUnsequencedCount}
          </Badge>
        </div>
      </div>

      {!hasBookings && !hasTasks && view === 'month' && (
        <div className="rounded-xl border bg-card px-4 py-10 text-center shadow-sm">
          <CalendarDays className="mx-auto h-8 w-8 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium">No visits or tasks</p>
          <p className="text-xs text-muted-foreground">Nothing scheduled for this view.</p>
        </div>
      )}

      {days.map((day) => {
        const key = toDateString(day);
        const dayBookings = bookingsByDay.get(key) ?? [];
        const dayTasks = tasksByDay.get(key) ?? [];
        const dayUnsequenced = agendaUnsequencedByDay.get(key) ?? [];

        if (
          view === 'month' &&
          dayBookings.length === 0 &&
          dayTasks.length === 0 &&
          dayUnsequenced.length === 0
        )
          return null;

        return (
          <section key={key} className="overflow-hidden rounded-xl border bg-card shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b bg-muted/40 px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{format(day, 'EEEE')}</p>
                <p className="text-xs text-muted-foreground">{format(day, 'MMM d, yyyy')}</p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {dayBookings.length + dayUnsequenced.length} visit
                {dayBookings.length + dayUnsequenced.length !== 1 ? 's' : ''}
                {dayTasks.length > 0
                  ? ` · ${dayTasks.length} task${dayTasks.length !== 1 ? 's' : ''}`
                  : ''}
              </span>
            </div>

            {dayBookings.length === 0 && dayTasks.length === 0 && dayUnsequenced.length === 0 ? (
              <div className="px-3 py-6 text-sm text-muted-foreground">No visits or tasks</div>
            ) : (
              <div className="divide-y">
                {dayTasks.length > 0 ? (
                  <div className="space-y-1 px-3 py-3">
                    {dayTasks.map((task) => (
                      <CalendarTaskChip
                        key={task.id}
                        task={task}
                        onClick={() => onTaskClick(task)}
                      />
                    ))}
                  </div>
                ) : null}
                {dayBookings.map((booking) => (
                  <MobileBookingCard
                    key={booking.bookingId}
                    booking={booking}
                    displayTz={displayTz}
                    onClick={() => onBookingClick(booking)}
                  />
                ))}
                {/* §15 — day-bound DAY_FLEXIBLE visits (no time yet): amber
                    row, click opens the same detail sheet. */}
                {dayUnsequenced.map((booking) => (
                  <button
                    key={booking.bookingId}
                    type="button"
                    onClick={() => onBookingClick(booking)}
                    className="flex w-full items-center gap-2 border-l-2 border-l-amber-400 bg-amber-50 px-3 py-2.5 text-left text-sm transition-colors hover:bg-amber-100 dark:bg-amber-950/40 dark:hover:bg-amber-950"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-amber-900 dark:text-amber-200">
                        {booking.customerName}
                      </span>
                      <span className="block truncate text-xs text-amber-800/80 dark:text-amber-300/80">
                        {booking.serviceType.name} · All Day
                      </span>
                      <JobTagChips tags={booking.tags} max={2} className="mt-1 flex-wrap" />
                    </span>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                      All Day
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function MobileBookingCard({
  booking,
  displayTz,
  onClick,
}: {
  booking: ScheduledBooking;
  displayTz: string;
  onClick: () => void;
}) {
  // Same Trade-palette resolver the map pins use, so a tile and its pin can
  // never disagree about a booking with no persisted job-type colour.
  const resolveJobTypeFamily = useJobTypeFamilyResolver();
  const startTime = formatDateTimeInTimeZone(booking.startAt, displayTz, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const [time, period] = startTime.split(/\s+/);

  return (
    <button
      type="button"
      onClick={onClick}
      style={bookingJobTypeStyle(booking, resolveJobTypeFamily)}
      className="grid w-full grid-cols-[72px_minmax(0,1fr)] gap-3 border-l-4 border-transparent px-3 py-3 text-left transition-[filter] hover:brightness-95"
    >
      <div className="rounded-lg bg-muted px-2 py-2 text-center">
        <p className="text-sm font-medium">{time || startTime}</p>
        {period && <p className="text-[11px] text-muted-foreground">{period}</p>}
      </div>
      <div className="min-w-0 space-y-1">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <p className="min-w-0 truncate text-sm font-medium">{booking.customerName}</p>
          <CalendarStatusPill status={booking.status} />
        </div>
        <JobTagChips tags={booking.tags} max={3} className="flex-wrap" />
        <p className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
          <JobTypeIcon icon={booking.jobType?.icon} />
          <span className="truncate">{booking.serviceType.name}</span>
        </p>
        <div className="flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex min-w-0 items-center gap-1">
            <Clock className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {formatTimeRange(booking.startAt, booking.endAt, displayTz)}
            </span>
          </span>
          {booking.technicianName && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{booking.technicianName}</span>
            </span>
          )}
        </div>
        {booking.address && (
          <p className="truncate text-xs text-muted-foreground">{booking.address}</p>
        )}
      </div>
    </button>
  );
}
